import { useState, useRef, useEffect, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../hooks/useChat';
import { useToast } from '../hooks/useToast';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Logo from './common/Logo';
import SidebarUserFooter from './common/SidebarUserFooter';
import ConfirmDialog from './common/ConfirmDialog';
import ProfileModal from './common/ProfileModal';
import AllUsersModal from './admin/AllUsersModal';
import SelectionOptions from './chat/SelectionOptions';
import { generateEstimationCsv, downloadCsv } from '../utils/estimationCsv';
import {
  appendTotalHoursRow,
  isPowerAppsPlatform,
  resolveEstimationSummaryTotals,
} from '../utils/estimationSummary';
import {
  saveEstimationItems,
  listChatHistories,
  getChatHistory,
  deleteChatHistory,
} from '../services/chatService';
import { STORAGE_KEYS } from '../core/config';
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_ERROR_MESSAGE,
  MAX_DOCUMENT_UPLOAD_BYTES,
  MAX_DOCUMENT_UPLOAD_MB,
  getDocumentTypeLabel,
  isLegacyDocFile,
  isSupportedDocumentUpload,
} from '../utils/documentUpload';

/** Session flag: estimation review popup at most once per tab (submit or dismiss). */
const ESTIMATION_REVIEW_SEEN_KEY = 'cettomate_estimation_review_seen';
const LEGACY_DASHBOARD_CHAT_ARCHIVES_KEY = 'cettomate_dashboard_chat_archives';
const DASHBOARD_EXPORTS_KEY = 'cettomate_dashboard_exports';
const DASHBOARD_RATINGS_KEY = 'cettomate_dashboard_ratings';

function readStoredItems(key) {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStoredItems(key, items) {
  try {
    window.localStorage.setItem(key, JSON.stringify(items));
  } catch {
    /* ignore storage failures */
  }
}

function prependStoredItem(key, item, limit = 12) {
  const current = readStoredItems(key);
  const next = [item, ...current].slice(0, limit);
  writeStoredItems(key, next);
  return next;
}

function archiveMessagePlainText(content) {
  if (content == null) return '';
  if (typeof content === 'string') {
    const trimmed = content.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return archiveMessagePlainText(JSON.parse(trimmed));
      } catch {
        return content;
      }
    }
    return content;
  }
  if (typeof content !== 'object') return String(content);

  if (content.type === 'selection') {
    const value = content.value;
    return Array.isArray(value) ? value.join(', ') : String(value ?? '').trim();
  }
  if (content.type === 'scope_edit') return '';
  if (content.type === 'estimation' || content.type === 'prompt') {
    return content.text || content.content || '';
  }
  return (
    content.text ||
    content.content ||
    (content.filename ? `[document] ${content.filename}` : '')
  );
}

function sanitizeArchiveMessages(messages) {
  if (!Array.isArray(messages)) return [];
  return messages
    .map((message, index) => {
      const content = archiveMessagePlainText(message?.content);
      if (message?.role === 'user' && !String(content || '').trim()) {
        return null;
      }
      return {
        ...message,
        role: message?.role || 'assistant',
        content,
        id: message?.id || `archive-${index}`,
        createdAt: message?.createdAt || message?.timestamp || new Date().toISOString(),
      };
    })
    .filter(Boolean);
}

function stripMarkdownBoldMarkers(text) {
  return String(text || '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .trim();
}

/** Map backend /chat/history row → dashboard list item (project tag + platform). */
function mapChatHistoryListItem(row) {
  const sessionId = row?.session_id ?? row?.sessionId ?? null;
  const tag = stripMarkdownBoldMarkers(row?.tag ?? '');
  const fallbackTitle = stripMarkdownBoldMarkers(row?.title ?? '');
  const title = tag || (fallbackTitle && fallbackTitle !== 'Chat' ? fallbackTitle : '');
  const platform = stripMarkdownBoldMarkers(row?.platform ?? '');
  const createdAt = row?.created_at ?? row?.createdAt ?? null;
  const messageCount = Number(row?.message_count ?? row?.messageCount ?? 0);

  return {
    title: title || 'Untitled chat',
    meta: platform || null,
    sessionId,
    createdAt,
    messageCount,
    source: row?.source ?? null,
  };
}

function normalizeChatHistoryList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(mapChatHistoryListItem)
    .filter((item) => item.sessionId && item.messageCount > 0)
    .sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
}

function extractHistoryMessages(data) {
  const snap = data?.chat_snapshot ?? data?.chatSnapshot;
  if (Array.isArray(snap?.messages) && snap.messages.length > 0) return snap.messages;
  if (Array.isArray(data?.messages)) return data.messages;
  return [];
}

function extractHistorySessionId(data, fallbackSessionId) {
  const snap = data?.chat_snapshot ?? data?.chatSnapshot;
  return (
    snap?.sessionId ??
    snap?.session_id ??
    data?.session_id ??
    data?.sessionId ??
    fallbackSessionId
  );
}

function extractHistorySlot(data, messages) {
  const snap = data?.chat_snapshot ?? data?.chatSnapshot;
  if (typeof snap?.slot === 'string' && snap.slot.trim()) return snap.slot.trim();
  if (typeof data?.slot === 'string' && data.slot.trim()) return data.slot.trim();
  const lastAssistant = [...messages]
    .reverse()
    .find((message) => message?.role === 'assistant' && typeof message?.slot === 'string');
  return lastAssistant?.slot?.trim() || null;
}

// SVG Icons
const SendIcon = () => (
  <svg
    className="send-btn-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#f8fafc"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" fill="#f8fafc" stroke="#f8fafc" />
  </svg>
);

const BotIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 4.5v3"></path>
    <path d="M7.5 8.5h9"></path>
    <path d="M6.5 12.25c0-1.519 1.231-2.75 2.75-2.75h5.5c1.519 0 2.75 1.231 2.75 2.75v2.5c0 1.519-1.231 2.75-2.75 2.75h-5.5c-1.519 0-2.75-1.231-2.75-2.75v-2.5Z"></path>
    <circle cx="9.75" cy="13.5" r="0.9" fill="currentColor" stroke="none"></circle>
    <circle cx="14.25" cy="13.5" r="0.9" fill="currentColor" stroke="none"></circle>
    <path d="M10 16.25h4"></path>
    <path d="M5 12h1.5"></path>
    <path d="M17.5 12H19"></path>
  </svg>
);


const SparklesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"></path>
    <path d="M12 21l-1.5-4.5L6 15l4.5-1.5L12 9l1.5 4.5L18 15l-4.5 1.5L12 21z"></path>
    <path d="M3 12l4.5 1.5L9 18l-1.5-4.5L3 9l4.5 1.5L9 3l-1.5 4.5L3 12z"></path>
    <path d="M21 12l-4.5-1.5L15 6l1.5 4.5L21 15l-4.5-1.5L15 21l1.5-4.5L21 12z"></path>
  </svg>
);

const LayoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="3" y1="9" x2="21" y2="9"></line>
    <line x1="9" y1="21" x2="9" y2="9"></line>
  </svg>
);

const ZapIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
);


const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const DeleteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

/**
 * Locks add/delete for this assistant bubble when:
 * - A later assistant message exists (next step delivered), or
 * - The user already sent the action that advances this step (immediate lock):
 *   - roles: "Continue"
 *   - modules: "Estimate" or "Estimation" (case-insensitive; matches option buttons)
 *   - features: "Confirm"
 * Silent in-place updates do not append messages, so edits stay allowed until then.
 */
function isStructuralEditLocked(messageIndex, messages, lockKind) {
  const list = messages ?? [];
  for (let i = messageIndex + 1; i < list.length; i++) {
    const m = list[i];
    if (m?.role === 'assistant') return true;
    if (m?.role !== 'user') continue;
    const t = String(m.content || '').trim();
    if (lockKind === 'roles' && t === 'Continue') return true;
    if (lockKind === 'modules') {
      const lower = t.toLowerCase();
      if (lower === 'estimate' || lower === 'estimation') return true;
    }
    if (lockKind === 'features' && t === 'Confirm') return true;
  }
  return false;
}

// Update the Message component condition to ensure it catches the estimation
function Message({
  message,
  onOptionClick,
  onConfirmScope,
  messages = [],
  messageIndex = 0,
  stepAnchorStages,
  sessionId,
  chatSlot,
  onDashboardSave,
  onDashboardExport,
  onDashboardRating,
  dashboardOwnerId,
  persistedEstimationIdRef,
}) {
  const isUser = message.role === 'user';

  // Features-review step: backend sends slot="features.review" between
  // "Estimate" (generate features) and hours. Markdown reuses "### 👤 **" / "📦"
  // like the modules screen, so it must be excluded from those checks below.
  const isFeaturesReview = !isUser && message.slot === 'features.review';

  // Detection logic for the summary card - check for new estimation format
  const isEstimation = !isUser && message.slot !== 'features.review' && (
    message.content.toLowerCase().includes('total estimated hours') ||
    message.content.toLowerCase().includes('total hours') ||
    message.content.includes('🚀 **Project Estimation & Scope of Work**') ||
    message.content.includes('### 📦 Key Modules') ||
    message.content.includes('### 🛠️ Detailed App Layers per Module') ||
    (message.content.includes('Project Estimation & Scope of Work') &&
      (message.content.includes('Detailed Scope') || message.content.includes('Frontend:') || message.content.includes('Backend/API:'))) ||
    (message.content.includes('Project Estimation & Scope of Work') && message.content.includes('Detailed Scope')) ||
    // Document upload review: scope lives in project_scope; body must still route to EstimationContent
    (message.slot === 'document_flow.review' &&
      message.project_scope &&
      typeof message.project_scope === 'object' &&
      Object.keys(message.project_scope).length > 0)
  );

  // Detection logic for Generated Modules and Features
  const isModulesFeatures = !isUser && (
    (message.content.includes('Modules:') && message.content.includes('Project Scope:')) ||
    (message.content.includes('Project Scope:') && (
      message.content.includes('Frontend:') ||
      message.content.includes('Backend/API:') ||
      message.content.includes('Core API and Admin Backend:') ||
      message.content.includes('Admin Panel Frontend:') ||
      message.content.includes('UI/UX:')
    ))
  );

  const featuresEditorMessage = (() => {
    if (!isFeaturesReview && !isModulesFeatures) return message;
    if (messageHasProjectScope(message)) return message;
    const parsedScope = parseFeaturesContentToScope(message.content);
    if (!scopeHasAnyFeatures(parsedScope)) return message;
    return { ...message, project_scope: parsedScope };
  })();
  const showFeaturesEditor =
    !isUser &&
    (isFeaturesReview || isModulesFeatures) &&
    message.slot !== 'estimation.final' &&
    messageHasProjectScope(featuresEditorMessage);

  // Detection logic for Roles (before modules generation)
  const isRoles = !isUser && (
    message.content.includes('👥 **Project Roles**') ||
    (message.slot === 'roles.items' && message.content.includes('•'))
  );

  // Detection logic for Role-Platform Assignment
  const isRolePlatformAssignment = !isUser && (
    message.slot === 'role_platform_assignments' ||
    (message.content.includes('**Roles**') && message.content.includes('**Select Platform for your roles**'))
  );

  // Explicit separation: multi-platform vs single-platform modules display
  // Multi-platform: backend sends "👥 **Role Platform Assignments**" and "📦 **Key Modules for {Platform}**"
  const isMultiPlatformModules = !isUser && (
    message.content.includes('Key Modules for') ||
    message.content.includes('Role Platform Assignments')
  );
  // Single-platform: backend sends "🚀 **Project Modules by Role**" and "📦 **Key Modules**" (no "for")
  const isSinglePlatformModules = !isUser && !isMultiPlatformModules && !isFeaturesReview && (
    message.content.includes('🚀 **Project Modules by Role**') ||
    (message.content.includes('### 👤 **') && message.content.includes('📦'))
  );
  // Legacy: any other role-modules-like content (e.g. slot or ### 👤 ** only)
  const isRoleModules = !isUser && !isMultiPlatformModules && !isFeaturesReview && (
    message.content.includes('🚀 **Project Modules by Role**') ||
    message.content.includes('### 👤 **')
  );

  let options = message.options && Array.isArray(message.options) ? message.options : null;

  // Multi-select detection: prefer backend selection_type, with slot fallback for known multi questions.
  const selectionType = message.selection_type;
  const multiSelectSlots = new Set([
    'platform.delivery_layers',
    'mobile.native.platforms',
  ]);
  const isMultiSelect = selectionType === 'multi' || multiSelectSlots.has(message.slot);

  const stepAnchorProps =
    stepAnchorStages?.length > 0 ? { 'data-stages': stepAnchorStages.join(' ') } : {};

  const rolesLocked = isStructuralEditLocked(messageIndex, messages, 'roles');
  const modulesLocked = isStructuralEditLocked(messageIndex, messages, 'modules');
  const featuresLocked = isStructuralEditLocked(messageIndex, messages, 'features');
  const isScopeWidthCard =
    isEstimation ||
    showFeaturesEditor ||
    isModulesFeatures ||
    isRoles ||
    isMultiPlatformModules ||
    isRolePlatformAssignment ||
    isSinglePlatformModules ||
    isRoleModules;

  return (
    <div
      className={`message-row ${isUser ? 'message-row-user' : ''}`}
      {...(message.id ? { 'data-message-id': message.id } : {})}
      {...stepAnchorProps}
    >
      {!isUser && (
        <div className="avatar avatar-assistant">
          <BotIcon />
        </div>
      )}

      <div className={`message-bubble ${isUser ? 'bubble-user' : 'bubble-assistant'} glass-bubble${isScopeWidthCard ? ' message-bubble-scope' : ''}`}>
        {isEstimation || showFeaturesEditor ? (
          <EstimationContent
            message={showFeaturesEditor ? featuresEditorMessage : message}
            onConfirmScope={onConfirmScope}
            sessionId={sessionId}
            structureLocked={featuresLocked}
            onDashboardSave={onDashboardSave}
            onDashboardExport={onDashboardExport}
            onDashboardRating={onDashboardRating}
            dashboardOwnerId={dashboardOwnerId}
            persistedEstimationIdRef={persistedEstimationIdRef}
            archiveMessages={messages}
            chatSlot={chatSlot}
          />
        ) : isModulesFeatures ? (
          <ModulesFeaturesContent
            message={message}
            onConfirmScope={onConfirmScope}
            structureLocked={featuresLocked}
          />
        ) : isRoles ? (
          <RolesContent
            message={message}
            onOptionClick={onOptionClick}
            messages={messages}
            structureLocked={rolesLocked}
          />
        ) : isMultiPlatformModules ? (
          <RolePlatformModulesContent message={message} onOptionClick={onOptionClick} structureLocked={modulesLocked} />
        ) : isRolePlatformAssignment ? (
          <RolePlatformAssignmentContent message={message} onOptionClick={onOptionClick} />
        ) : isSinglePlatformModules || isRoleModules ? (
          <InteractiveRoleModulesContent
            message={message}
            onOptionClick={onOptionClick}
            structureLocked={modulesLocked}
          />
        ) : message.role === 'assistant' && message.content.includes('Got it!') ? (
          <SummaryContent message={message} />
        ) : (
          <div className="message-content">
            <Markdown remarkPlugins={[remarkGfm]}>
              {message.content}
            </Markdown>
          </div>
        )}

        {/* Use SelectionOptions component for proper disable logic */}
        {/* Hide when estimation/features editor has Confirm inside EditableEstimationContent */}
        {!isUser && options && options.length > 0 && !isRolePlatformAssignment && !(
          ((isEstimation || showFeaturesEditor) && messageHasProjectScope(isEstimation ? message : featuresEditorMessage))
        ) && (
          <SelectionOptions
            options={options}
            messageId={message.id}
            disabled={false}
            allowMultiple={isMultiSelect} // Only true when selection_type is "multi"
            showContinueButton={isMultiSelect} // Only true when selection_type is "multi"
            preselected={message.preselected_options} // Deliverables a named tech already answered - locked, pre-checked
            slot={message.slot} // Pass slot from message for Continue button
            onContinue={(value) => {
              onOptionClick(value, message.slot);
            }}
          />
        )}
      </div>
    </div>
  );
}

// Summary content component (Got it! format)
function SummaryContent({ message }) {
  const content = message.content;

  // Parse content to match backend format exactly
  // Format: "Got it! First line content.\n\nReview section...\n\nQuestion?"
  const parseContent = (text) => {
    // Remove "Got it!" prefix (case insensitive)
    const withoutPrefix = text.replace(/^Got it!\s*/i, '').trim();

    // Extract first line (up to first newline)
    // Then handle period removal carefully to preserve file extensions like "React.js"
    const newlineMatch = withoutPrefix.match(/^([^\n]+)/);
    let firstLine = newlineMatch ? newlineMatch[1].trim() : null;

    // Remove trailing period if it exists (sentence-ending period)
    // Always remove trailing period, even after file extensions like "Node.js." -> "Node.js"
    if (firstLine && firstLine.endsWith('.')) {
      // Remove the trailing period (sentence-ending period)
      // This handles both regular periods and periods after file extensions like "Node.js."
      firstLine = firstLine.slice(0, -1).trim();
    }

    // Find the question (last line that ends with ? or starts with question keywords)
    const questionKeywords = /^(Which|Do you|What|How|Are you|Would you|Can you|Should you|Will you|Is there|Are there|Have you|Did you|Does|Is|Are|Will|Can|Should|Please)/i;

    // Keep original lines with all formatting and empty lines
    const allLinesOriginal = withoutPrefix.split('\n');
    const allLinesTrimmed = allLinesOriginal.map(l => l.trim());

    let questionIndex = -1;
    let question = null;

    // Look for question from the end (using trimmed lines for matching)
    for (let i = allLinesTrimmed.length - 1; i >= 0; i--) {
      const line = allLinesTrimmed[i];
      if (line && (line.endsWith('?') || questionKeywords.test(line))) {
        question = line;
        questionIndex = i; // Use index from allLinesOriginal
        break;
      }
    }

    // Everything between first line and question is the review section
    let reviewSection = null;
    if (questionIndex >= 0) {
      // Find where first line ends (should be first non-empty line that matches)
      let firstLineIndex = -1;
      if (firstLine) {
        for (let i = 0; i < allLinesTrimmed.length; i++) {
          const line = allLinesTrimmed[i];
          if (line && (line === firstLine || line.toLowerCase() === firstLine.toLowerCase() ||
            line.startsWith(firstLine) || firstLine.startsWith(line))) {
            firstLineIndex = i;
            break;
          }
        }
      }

      // Extract lines between first line and question
      // Start from after the first line (or from beginning if firstLineIndex not found)
      const startIndex = firstLineIndex >= 0 ? firstLineIndex + 1 : 0;
      const reviewLines = [];

      for (let i = startIndex; i < questionIndex; i++) {
        // Include all lines (including empty ones) to preserve exact format
        // Only skip if it's an exact duplicate of the first line
        const trimmedLine = allLinesTrimmed[i];
        if (!firstLine || (trimmedLine !== firstLine && trimmedLine.toLowerCase() !== firstLine.toLowerCase())) {
          reviewLines.push(allLinesOriginal[i]);
        }
      }

      const reviewText = reviewLines.join('\n');
      // Set reviewSection if it has content
      if (reviewText.trim().length > 0) {
        reviewSection = reviewText;
      }
    }

    return {
      firstLine: firstLine,
      reviewSection: reviewSection || null,
      question: question
    };
  };

  const { firstLine, reviewSection, question } = parseContent(content);

  return (
    <div className="summary-message">
      <div className="summary-message-header">
        <div className="summary-message-icon-wrapper">
          <span className="summary-message-icon"><CheckIcon /></span>
        </div>
        <span className="summary-message-title">Got it!</span>
        {firstLine && (
          <span className="summary-message-context">
            {firstLine}
          </span>
        )}
      </div>
      {reviewSection && (
        <div className="summary-message-other-context">
          <Markdown remarkPlugins={[remarkGfm]}>
            {reviewSection}
          </Markdown>
        </div>
      )}
      {question && (
        <div className="summary-message-question">
          <Markdown remarkPlugins={[remarkGfm]}>
            {question}
          </Markdown>
        </div>
      )}
    </div>
  );
}

// Layer keys and display labels for scope editing
const SCOPE_LAYERS = ['frontend', 'core_and_admin_api', 'admin_panel_frontend', 'ui_ux'];
const SCOPE_LAYER_LABELS = {
  frontend: 'Frontend',
  core_and_admin_api: 'Backend/API',
  admin_panel_frontend: 'Admin Panel',
  ui_ux: 'UI/UX'
};

const SCOPE_LAYERS_POWER_APPS = ['database', 'ui_ux'];
const SCOPE_LAYER_LABELS_POWER_APPS = { database: 'Development', ui_ux: 'UI/UX' };

/** Matches backend PlanService.SHARED_ADMIN_PANEL_ROLE — admin features bucket below other roles. */
const SHARED_ADMIN_PANEL_ROLE = 'Admin Panel (Shared)';

function getScopeLayersForPlatform(platform) {
  const isPowerApps = platform === 'Power Apps';
  return {
    layerOrder: isPowerApps ? SCOPE_LAYERS_POWER_APPS : SCOPE_LAYERS,
    layerLabels: isPowerApps ? SCOPE_LAYER_LABELS_POWER_APPS : SCOPE_LAYER_LABELS
  };
}

// Deep clone project_scope and normalize structure (role -> module -> layer -> string[])
function normalizeScope(scope, platform = '') {
  if (!scope || typeof scope !== 'object') return {};
  const { layerOrder } = getScopeLayersForPlatform(platform);
  const validLayers = new Set(layerOrder);
  const out = {};
  for (const [role, modules] of Object.entries(scope)) {
    if (!modules || typeof modules !== 'object') continue;
    out[role] = {};
    for (const [moduleName, layers] of Object.entries(modules)) {
      if (!layers || typeof layers !== 'object') continue;
      out[role][moduleName] = {};
      // Only include app-layer keys that exist on the API payload. Padding every layer with []
      // made unselected delivery layers (e.g. Admin Panel) show as empty sections with "+".
      const incomingKeys = Object.keys(layers).filter((k) => validLayers.has(k));
      const keysToNormalize = incomingKeys.length > 0
        ? incomingKeys
        : Object.keys(layers).length > 0
          ? Object.keys(layers)
          : layerOrder;
      for (const layer of keysToNormalize) {
        const feats = layers[layer];
        out[role][moduleName][layer] = Array.isArray(feats)
          ? feats.filter(f => f != null && String(f).trim() !== '')
          : [];
      }
    }
  }
  return out;
}

/** Backend orders shared admin last; JSON parse keeps key order, but this enforces it. */
function getOrderedScopeEntries(scope) {
  if (!scope || typeof scope !== 'object') return [];
  const entries = Object.entries(scope);
  const idx = entries.findIndex(([roleName]) => roleName === SHARED_ADMIN_PANEL_ROLE);
  if (idx === -1) return entries;
  const next = [...entries];
  const [sharedEntry] = next.splice(idx, 1);
  return [...next, sharedEntry];
}

const FEATURE_LAYER_LABEL_TO_KEY = {
  Frontend: 'frontend',
  'Backend/API': 'core_and_admin_api',
  Backend: 'core_and_admin_api',
  'Admin Panel': 'admin_panel_frontend',
  'Admin Panel Frontend': 'admin_panel_frontend',
  'Core API and Admin Backend': 'core_and_admin_api',
  'UI/UX': 'ui_ux',
  Development: 'database',
  'Development (Power Apps)': 'database',
  'Development (app type)': 'database',
  'Add-ins': 'Add-ins',
};

function matchFeatureLayerKey(line) {
  const cleaned = String(line || '')
    .replace(/\*/g, '')
    .replace(/^#+\s*/, '')
    .replace(/:$/, '')
    .trim();
  return FEATURE_LAYER_LABEL_TO_KEY[cleaned] || null;
}

function messageHasProjectScope(message) {
  const scope = message?.project_scope;
  return Boolean(scope && typeof scope === 'object' && Object.keys(scope).length > 0);
}

/**
 * Build role → module → layer → features from features-review / Project Scope markdown
 * when the backend omits project_scope.
 */
function parseFeaturesContentToScope(text) {
  if (!text) return {};
  const lines = String(text).split('\n');
  const scope = {};
  let currentRole = null;
  let currentModule = null;
  let currentLayer = null;
  let inProjectScope = false;

  const ensureLayer = () => {
    if (!currentRole) currentRole = 'User';
    if (!scope[currentRole]) scope[currentRole] = {};
    if (!currentModule) return;
    if (!scope[currentRole][currentModule]) scope[currentRole][currentModule] = {};
    if (!currentLayer) return;
    if (!scope[currentRole][currentModule][currentLayer]) {
      scope[currentRole][currentModule][currentLayer] = [];
    }
  };

  for (const originalLine of lines) {
    const line = originalLine.trim();
    if (!line || /^[-_=]{3,}$/.test(line)) continue;
    if (/^modules:\s*$/i.test(line)) {
      inProjectScope = false;
      continue;
    }
    if (
      /^project scope:\s*$/i.test(line) ||
      (line.includes('Detailed Scope') && !line.includes('Project Estimation'))
    ) {
      inProjectScope = true;
      currentModule = null;
      currentLayer = null;
      continue;
    }
    if (/^(\*\*)?project:/i.test(line) || /^(\*\*)?platform:/i.test(line)) continue;
    if (line.toLowerCase().includes('you can ask questions')) continue;

    const roleMatch =
      line.match(/###\s*👤\s*\*{0,2}(.+?)\*{0,2}\s*$/) ||
      line.match(/^👤\s+\*{0,2}(.+?)\*{0,2}\s*$/);
    if (roleMatch) {
      currentRole = roleMatch[1].replace(/\*/g, '').trim();
      currentModule = null;
      currentLayer = null;
      inProjectScope = true;
      continue;
    }

    const layerKey = matchFeatureLayerKey(line);
    if (layerKey && (inProjectScope || currentRole || currentModule)) {
      if (!currentModule) continue;
      currentLayer = layerKey;
      ensureLayer();
      continue;
    }

    const numberedModule = line.match(/^\d+\.\s+(.+)$/);
    if (numberedModule) {
      currentModule = numberedModule[1].replace(/\*/g, '').trim();
      currentLayer = null;
      if (currentModule) ensureLayer();
      continue;
    }

    const h3Module = line.match(/^###\s+(?!👤)(.+)$/);
    if (h3Module && !matchFeatureLayerKey(h3Module[1])) {
      currentModule = h3Module[1].replace(/\*/g, '').trim();
      currentLayer = null;
      if (currentModule) ensureLayer();
      continue;
    }

    const featureMatch =
      line.match(/^[-•*–]\s+(.+)$/) ||
      originalLine.match(/^\s{2,}[-•*–]\s+(.+)$/);
    if (featureMatch && currentModule) {
      if (!currentLayer) currentLayer = 'frontend';
      const featureName = featureMatch[1].replace(/\*/g, '').replace(/^_|_$/g, '').trim();
      if (featureName && !/^_?no features/i.test(featureName)) {
        ensureLayer();
        scope[currentRole][currentModule][currentLayer].push(featureName);
      }
      continue;
    }

    if (
      inProjectScope &&
      !line.endsWith(':') &&
      !line.startsWith('#') &&
      !/^[-•*]/.test(line) &&
      line.length > 2
    ) {
      currentModule = line.replace(/\*/g, '').trim();
      currentLayer = null;
      if (currentModule) ensureLayer();
    }
  }

  return scope;
}

function scopeHasAnyFeatures(scope) {
  if (!scope || typeof scope !== 'object') return false;
  return Object.values(scope).some((modules) =>
    Object.values(modules || {}).some((layers) =>
      Object.values(layers || {}).some((feats) => Array.isArray(feats) && feats.some((f) => String(f || '').trim()))
    )
  );
}

/**
 * Layers to show for one module: only keys present on the payload, in platform order.
 * Empty layers stay visible while editing so the add button remains after the last delete.
 */
function getVisibleLayerKeysForModule(layers, layerOrder, includeEmpty = false) {
  if (!layers || typeof layers !== 'object') return [];
  const extras = Object.keys(layers).filter((k) => !layerOrder.includes(k));
  const keys = [...layerOrder.filter((k) => Object.prototype.hasOwnProperty.call(layers, k)), ...extras];
  return keys.filter((k) => {
    if (includeEmpty) return true;
    const list = layers[k];
    return Array.isArray(list) && list.some((f) => f != null && String(f).trim() !== '');
  });
}

// Editable scope UI: add/delete features per layer, Confirm sends updated_project_scope
function EditableEstimationContent({ message, onConfirmScope, structureLocked = false }) {
  const { toastMessage, showToast } = useToast();
  const projectMatch = message.content.match(/\*\*Project:\*\*\s*(.+?)(?:\n|$)/i) || message.content.match(/^Project:\s*(.+?)(?:\n|$)/i);
  const platformMatch = message.content.match(/\*\*Platform:\*\*\s*(.+?)(?:\n|$)/i) || message.content.match(/^Platform:\s*(.+?)(?:\n|$)/i);
  const project = projectMatch ? projectMatch[1].trim() : '';
  const platform = platformMatch ? platformMatch[1].trim() : '';
  const { layerOrder, layerLabels } = getScopeLayersForPlatform(platform);
  const initialScope = message.project_scope ? normalizeScope(message.project_scope, platform) : {};
  const [scope, setScope] = useState(() => JSON.parse(JSON.stringify(initialScope)));
  const isFeaturesStep =
    message.slot === 'features.review' ||
    (String(message.content || '').includes('Project Scope:') &&
      !String(message.content || '').includes('Project Estimation'));
  const confirmLabel = (Array.isArray(message.options) ? message.options : []).find((opt) => opt && opt !== 'Cancel') || 'Confirm';
  const orderedScopeEntries = getOrderedScopeEntries(scope);
  const hideSyntheticRole =
    orderedScopeEntries.length === 1 &&
    orderedScopeEntries[0]?.[0] === 'User' &&
    !String(message.content || '').includes('👤');
  const [addModal, setAddModal] = useState({ open: false, role: null, module: null, layer: null, anchor: null });
  const [addFeatureInput, setAddFeatureInput] = useState('');
  const addFeatureInputRef = useRef(null);
  const MODAL_WIDTH = 320;
  const MODAL_MIN_EDGE = 12;

  const removeFeature = (role, moduleName, layer, index) => {
    if (structureLocked) return;
    const featureName = scope[role]?.[moduleName]?.[layer]?.[index];
    setScope(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next[role]?.[moduleName]?.[layer]) return prev;
      next[role][moduleName][layer] = next[role][moduleName][layer].filter((_, i) => i !== index);
      return next;
    });
    if (featureName) {
      showToast(`Feature "${featureName}" deleted successfully`);
    }
  };

  const addFeature = (role, moduleName, layer, name) => {
    if (structureLocked) return;
    const trimmed = String(name).trim();
    if (!trimmed) return;
    const existing = scope[role]?.[moduleName]?.[layer] ?? [];
    const isDuplicate = existing.some(
      (f) => String(f).trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) {
      showToast('This feature already exists');
      return;
    }
    setScope(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next[role]) next[role] = {};
      if (!next[role][moduleName]) next[role][moduleName] = {};
      if (!next[role][moduleName][layer]) next[role][moduleName][layer] = [];
      next[role][moduleName][layer].push(trimmed);
      return next;
    });
    setAddFeatureInput('');
    setAddModal({ open: false, role: null, module: null, layer: null, anchor: null });
    document.body.style.overflow = '';
    showToast(`Feature "${trimmed}" added successfully`);
  };

  const closeAddModal = () => {
    setAddModal(prev => ({ ...prev, open: false, anchor: null }));
    document.body.style.overflow = '';
  };

  const openAddModal = (role, moduleName, layer, ev) => {
    if (structureLocked) return;
    ev?.preventDefault?.();
    ev?.stopPropagation?.();
    const anchor = ev?.currentTarget ? (() => {
      const rect = ev.currentTarget.getBoundingClientRect();
      let left = rect.right + 12;
      let top = rect.top;
      if (left + MODAL_WIDTH + MODAL_MIN_EDGE > window.innerWidth) left = window.innerWidth - MODAL_WIDTH - MODAL_MIN_EDGE;
      if (left < MODAL_MIN_EDGE) left = MODAL_MIN_EDGE;
      if (top + 220 > window.innerHeight) top = window.innerHeight - 220;
      if (top < MODAL_MIN_EDGE) top = MODAL_MIN_EDGE;
      return { left, top };
    })() : null;
    setAddModal({ open: true, role, module: moduleName, layer, anchor });
    setAddFeatureInput('');
    document.body.style.overflow = 'hidden';
  };

  useEffect(() => {
    if (addModal.open && addFeatureInputRef.current) {
      const t = requestAnimationFrame(() => {
        addFeatureInputRef.current?.focus({ preventScroll: true });
      });
      return () => cancelAnimationFrame(t);
    }
  }, [addModal.open]);

  const handleConfirm = () => {
    if (structureLocked) return;
    if (onConfirmScope) onConfirmScope(scope, confirmLabel);
  };

  return (
    <div className="estimation-display new-estimation-format editable-scope-display">
      <div className="estimation-header">
        <div className="estimation-header-content">
          <h2>{isFeaturesStep ? 'Project Features' : 'Project Estimation & Scope of Work'}</h2>
          {(project || platform) && (
            <div className="estimation-project-info">
              {project && <span className="project-name"><strong>Project:</strong> {project}</span>}
              {platform && <span className="platform-name"><strong>Platform:</strong> {platform}</span>}
            </div>
          )}
        </div>
      </div>
      <div className="estimation-body" data-chat-subanchor="features" tabIndex={-1}>
        <h3 className="estimation-section-title">Detailed Scope</h3>
        {getOrderedScopeEntries(scope).map(([roleName, modules]) => {
          if (!modules || typeof modules !== 'object') return null;
          const moduleBlocks = Object.entries(modules).map(([moduleName, layers]) => {
            const visibleLayerKeys = getVisibleLayerKeysForModule(layers, layerOrder);
            if (visibleLayerKeys.length === 0) return null;
            return (
              <div key={moduleName} className="estimation-module-numbered">
                <div className="module-heading-shell">
                  <div className="module-heading-copy">
                    <span className="module-heading-kicker">Module block</span>
                    <h5 className="module-name-numbered">{moduleName}</h5>
                  </div>
                  <span className="module-layer-count-badge">
                    {visibleLayerKeys.length} {visibleLayerKeys.length === 1 ? 'layer' : 'layers'}
                  </span>
                </div>
                {visibleLayerKeys.map((layerKey) => {
                  const features = layers[layerKey] || [];
                  return (
                    <div key={layerKey} className="layer-section-numbered layer-section-editable">
                      <div className="layer-shell">
                        <div className={structureLocked ? 'layer-label-numbered' : 'layer-label-numbered layer-label-with-add'}>
                          <span className="layer-label-text app-layer-label">{layerLabels[layerKey] || layerKey}</span>
                          <span className="layer-feature-count-badge">
                            {features.length} {features.length === 1 ? 'feature' : 'features'}
                          </span>
                        </div>
                        {!structureLocked && (
                          <button
                            type="button"
                            className="add-feature-btn"
                            onClick={(e) => openAddModal(roleName, moduleName, layerKey, e)}
                            title="Add feature"
                          >
                            +
                          </button>
                        )}
                      </div>
                      <ul
                        className={
                          structureLocked ? 'features-list-numbered' : 'features-list-numbered features-list-editable'
                        }
                      >
                        {features.map((feature, idx) => (
                          <li
                            key={idx}
                            className={
                              structureLocked ? 'feature-item-numbered' : 'feature-item-numbered feature-item-editable'
                            }
                          >
                            <span className="feature-item-text">{feature}</span>
                            {!structureLocked && (
                              <button
                                type="button"
                                className="delete-feature-btn"
                                onClick={() => removeFeature(roleName, moduleName, layerKey, idx)}
                                title="Delete feature"
                              >
                                <DeleteIcon />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            );
          });
          if (!moduleBlocks.some(Boolean)) return null;
          return (
            <div key={roleName} className="estimation-role-section">
              {!hideSyntheticRole && <h4 className="role-name-simple">👤 {roleName}</h4>}
              {moduleBlocks}
            </div>
          );
        })}
        {!structureLocked && (
          <div className="editable-scope-actions">
            <button type="button" className="confirm-scope-btn" onClick={handleConfirm}>
              {confirmLabel}
            </button>
          </div>
        )}
      </div>

      {/* Add Feature modal: rendered in portal so it's not clipped by parent overflow/transform */}
      {!structureLocked && addModal.open && createPortal(
        <div className="add-feature-modal-overlay" onClick={closeAddModal}>
          <div
            className="add-feature-modal add-feature-modal-theme"
            style={addModal.anchor ? { left: addModal.anchor.left, top: addModal.anchor.top } : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
            onClick={e => e.stopPropagation()}
          >
            <button type="button" className="add-feature-modal-close" onClick={closeAddModal} aria-label="Close">
              ×
            </button>
            <div className="add-feature-modal-header">
              <span className="add-feature-modal-kicker">Scope editor</span>
              <h4 className="add-feature-modal-title">Add feature</h4>
            </div>
            <input
              ref={addFeatureInputRef}
              type="text"
              className="add-feature-modal-input"
              placeholder="Feature name..."
              value={addFeatureInput}
              onChange={e => setAddFeatureInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addFeature(addModal.role, addModal.module, addModal.layer, addFeatureInput); }}
            />
            <button
              type="button"
              className="add-feature-modal-submit"
              onClick={() => addFeature(addModal.role, addModal.module, addModal.layer, addFeatureInput)}
              disabled={!addFeatureInput.trim()}
            >
              Add Feature
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Toast for add/delete feature feedback (same style as module add/delete) */}
      {toastMessage && createPortal(
        <div className="module-toast">
          <div className="module-toast-content">
            <span className="module-toast-icon">✓</span>
            <span className="module-toast-message">{toastMessage}</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Estimation content component - Handles both old and new formats
function EstimationContent({
  message,
  onConfirmScope,
  sessionId,
  structureLocked = false,
  onDashboardSave,
  onDashboardExport,
  onDashboardRating,
  dashboardOwnerId,
  persistedEstimationIdRef,
  archiveMessages,
  chatSlot,
}) {
  const content = message.content;

  // Editable scope until hours are finalized (Confirm); estimation.final shows read-only + CSV
  const hasEditableScope =
    message.project_scope &&
    typeof message.project_scope === 'object' &&
    Object.keys(message.project_scope).length > 0 &&
    message.slot !== 'estimation.final';
  if (hasEditableScope && onConfirmScope) {
    return (
      <EditableEstimationContent
        message={message}
        onConfirmScope={onConfirmScope}
        structureLocked={structureLocked}
      />
    );
  }

  // Check if it's the new format (Project Estimation & Scope of Work)
  // Support both old format (with markdown) and new format (without markdown)
  const isNewFormat = content.includes('Project Estimation & Scope of Work') ||
    content.includes('📦') ||
    content.includes('🛠️') ||
    content.includes('Key Modules') ||
    content.includes('Detailed App Layers per Module') ||
    content.includes('Detailed Scope');

  if (isNewFormat) {
    return (
      <NewEstimationContent
        content={content}
        estimationValue={message.estimation_value}
        featureOptions={message.feature_options ?? null}
        estimationOptions={message.estimation_options ?? null}
        qaHoursFromApi={message.qa_hours}
        estimationSummary={message.estimation_summary ?? null}
        totalHoursDict={message.total_hours_dict ?? null}
        moduleEstimatedHours={message.module_estimated_hours ?? null}
        phases={message.phases ?? null}
        sessionId={sessionId}
        onDashboardSave={onDashboardSave}
        onDashboardExport={onDashboardExport}
        onDashboardRating={onDashboardRating}
        dashboardOwnerId={dashboardOwnerId}
        persistedEstimationIdRef={persistedEstimationIdRef}
        archiveMessages={archiveMessages}
        chatSlot={chatSlot}
      />
    );
  }

  // Old format - simple category: hours
  const lines = content.split('\n').filter(line => line.includes(':'));
  const totalLine = lines.find(line => line.toLowerCase().includes('total'));
  const categoryLines = lines.filter(line => !line.toLowerCase().includes('total'));

  const parseLine = (line) => {
    const parts = line.split(':');
    return {
      name: parts[0].replace(/[*-]/g, '').trim(),
      hours: parts[1] ? parts[1].trim() : ''
    };
  };

  return (
    <div className="estimation-display">
      <div className="estimation-header">
        <div className="summary-message-icon-wrapper">
          <ZapIcon />
        </div>
        <h2>Estimation</h2>
      </div>

      <div className="estimation-body">
        {categoryLines.map((line, idx) => {
          const { name, hours } = parseLine(line);
          return (
            <div key={idx} className="estimation-category">
              <span className="category-name">{name}</span>
              <span className="category-hours">{hours}</span>
            </div>
          );
        })}

        {totalLine && (
          <div className="estimation-total">
            <span className="total-label">Total Estimated Hours</span>
            <span className="total-value">{parseLine(totalLine).hours}</span>
          </div>
        )}
      </div>

      <div className="estimation-hint">
        <span className="hint-text">You can ask questions about this estimation anytime!</span>
      </div>
    </div>
  );
}

// Parse backend format: App layer totals (e.g. "Frontend (X) : 80 hrs") and module totals ("1. Module — 80 hrs")
function parseEstimationSummarySections(content) {
  const appLayerTotals = [];
  const moduleTotals = [];
  const normalized = (content || '').replace(/\r\n/g, '\n').trim();
  const lines = normalized.split('\n');
  let seenModulesHeader = false;
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!trimmed) continue;
    if (/\*\*Modules\s*:\*\*/.test(trimmed) || /^Modules\s*:\s*$/.test(trimmed) || /^Modules\s*:/.test(trimmed)) {
      seenModulesHeader = true;
      continue;
    }
    if (/🛠️\s*\*{0,2}\s*Detailed Scope/i.test(trimmed) || (trimmed.includes('Detailed Scope') && trimmed.length < 60)) {
      break;
    }
    if (!seenModulesHeader) {
      const layerMatch = trimmed.match(/^(.+?)\s*:\s*(\d+(?:\.\d+)?)\s*hrs\s*$/);
      if (layerMatch && !/^\d+\.\s/.test(trimmed) && !/^\*\*Project\*\*|^\*\*Platform\*\*|^Project:\s*|^Platform:\s*/.test(trimmed)) {
        const label = layerMatch[1].replace(/\*\*/g, '').trim();
        const hours = label === 'QA' ? Math.round(parseFloat(layerMatch[2])) : parseFloat(layerMatch[2]);
        appLayerTotals.push({ label, hours });
      }
    } else {
      const moduleMatch = trimmed.match(/^\d+\.\s+(.+?)\s*[—\-]\s*(\d+)\s*hrs\s*$/);
      if (moduleMatch) {
        moduleTotals.push({ name: moduleMatch[1].replace(/\*\*/g, '').trim(), hours: parseInt(moduleMatch[2], 10) });
      }
    }
  }
  return { appLayerTotals: appendTotalHoursRow(appLayerTotals), moduleTotals };
}

// Build the per-phase "Detailed Scope" sections directly from a phase's structured
// estimation_value. Markdown parsing of the combined ai_reply does not preserve phase
// boundaries, so we render each phase's modules/features/screens from its own value.
//
// Office Add-ins value is keyed by deliverable ({ "Add-ins"/"Admin Panel"/"UI/UX":
// { module: [feats] } }); other platforms are keyed by app layer ({ "frontend"/
// "core_and_admin_api"/"admin_panel_frontend"/"ui_ux"/"database": { module: [feats] } }).
// Both are normalised to a common shape so the same JSX renders every platform:
//   [{ title, modules: [{ title, hours, groups: [{ label, features: [{name, hours}] }] }] }]
const DETAILED_SCOPE_DELIVERABLE_ORDER = ['Add-ins', 'Admin Panel', 'UI/UX'];
const DETAILED_SCOPE_LAYER_ORDER = [
  'frontend',
  'core_and_admin_api',
  'admin_panel_frontend',
  'admin_panel_backend',
  'database',
];
const DETAILED_SCOPE_LAYER_LABELS = {
  frontend: 'Frontend',
  core_and_admin_api: 'Backend / API',
  admin_panel_frontend: 'Admin Panel',
  admin_panel_backend: 'Admin Panel API',
  database: 'Development',
  ui_ux: 'UI/UX',
};

function normalizeScopeFeatures(feats) {
  return (Array.isArray(feats) ? feats : [])
    .map((f) => ({
      name: (f?.feature_name ?? '').toString().trim(),
      hours: Number(f?.estimated_hours) || 0,
    }))
    .filter((f) => f.name);
}

// Normalize a module/feature title for option-group matching (mirrors backend
// _normalize_lookup_text: lowercase, strip non-alphanumerics, collapse spaces).
function normalizeOptionKey(value) {
  return (value ?? '')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// Regroup a module's features into "Option 1 / Option 2" sub-groups when the
// backend reports alternative implementation choices for that module. Both
// options are kept (their hours are already summed into the module total).
function applyFeatureOptionsToModule(moduleBlock, optionGroups) {
  if (!Array.isArray(optionGroups) || optionGroups.length === 0) return moduleBlock;
  const allFeatures = [];
  for (const group of moduleBlock.groups || []) {
    for (const f of group.features || []) allFeatures.push(f);
  }
  const grouped = new Set();
  optionGroups.forEach((g) => {
    (g?.features || []).forEach((name) => grouped.add(normalizeOptionKey(name)));
  });
  const leftover = allFeatures.filter((f) => !grouped.has(normalizeOptionKey(f.name)));
  const newGroups = [];
  if (leftover.length > 0) newGroups.push({ label: null, features: leftover });
  optionGroups.forEach((g) => {
    const wanted = new Set((g?.features || []).map(normalizeOptionKey));
    const feats = allFeatures.filter((f) => wanted.has(normalizeOptionKey(f.name)));
    if (feats.length > 0) {
      newGroups.push({ label: (g?.label || '').toString().trim() || 'Option', features: feats });
    }
  });
  return { ...moduleBlock, groups: newGroups.length > 0 ? newGroups : moduleBlock.groups };
}

function buildDetailedScopeFromValue(estimationValue, featureOptions) {
  if (!estimationValue || typeof estimationValue !== 'object') return [];
  // Map normalized module name -> option groups for quick lookup.
  const optionByModule = new Map();
  if (featureOptions && typeof featureOptions === 'object') {
    for (const [moduleName, groups] of Object.entries(featureOptions)) {
      if (Array.isArray(groups) && groups.length > 0) {
        optionByModule.set(normalizeOptionKey(moduleName), groups);
      }
    }
  }
  const withOptions = (moduleBlock) => {
    const groups = optionByModule.get(normalizeOptionKey(moduleBlock.title));
    return groups ? applyFeatureOptionsToModule(moduleBlock, groups) : moduleBlock;
  };
  const keys = Object.keys(estimationValue);
  const isOffice = keys.some((k) => DETAILED_SCOPE_DELIVERABLE_ORDER.includes(k));

  // Office Add-ins: section = deliverable, each module shows a flat feature list.
  if (isOffice) {
    const order = [
      ...DETAILED_SCOPE_DELIVERABLE_ORDER.filter((k) => estimationValue[k]),
      ...keys.filter((k) => !DETAILED_SCOPE_DELIVERABLE_ORDER.includes(k)),
    ];
    const sections = [];
    for (const deliverable of order) {
      const modules = estimationValue[deliverable];
      if (!modules || typeof modules !== 'object') continue;
      const moduleBlocks = [];
      for (const [moduleName, feats] of Object.entries(modules)) {
        const features = normalizeScopeFeatures(feats);
        if (features.length === 0) continue;
        moduleBlocks.push(withOptions({
          title: (moduleName && moduleName.trim()) || deliverable,
          hours: features.reduce((sum, f) => sum + f.hours, 0),
          groups: [{ label: null, features }],
        }));
      }
      if (moduleBlocks.length > 0) sections.push({ title: deliverable, modules: moduleBlocks });
    }
    return sections;
  }

  // Other platforms: group by module, then by app layer (Frontend, Backend/API, ...).
  const moduleOrder = [];
  const moduleMap = new Map(); // moduleName -> [{ label, features }]
  const layerKeys = [
    ...DETAILED_SCOPE_LAYER_ORDER.filter((k) => estimationValue[k]),
    ...keys.filter((k) => k !== 'ui_ux' && !DETAILED_SCOPE_LAYER_ORDER.includes(k)),
  ];
  for (const layerKey of layerKeys) {
    const modules = estimationValue[layerKey];
    if (!modules || typeof modules !== 'object') continue;
    for (const [moduleName, feats] of Object.entries(modules)) {
      const features = normalizeScopeFeatures(feats);
      if (features.length === 0) continue;
      const name = (moduleName && moduleName.trim()) || 'General';
      if (!moduleMap.has(name)) {
        moduleMap.set(name, []);
        moduleOrder.push(name);
      }
      moduleMap.get(name).push({
        label: DETAILED_SCOPE_LAYER_LABELS[layerKey] || layerKey,
        features,
      });
    }
  }

  const sections = [];
  const moduleBlocks = moduleOrder.map((name) => {
    const groups = moduleMap.get(name);
    const hours = groups.reduce(
      (sum, g) => sum + g.features.reduce((s, f) => s + f.hours, 0),
      0
    );
    return withOptions({ title: name, hours, groups });
  });
  if (moduleBlocks.length > 0) sections.push({ title: null, modules: moduleBlocks });

  // UI/UX screens render as their own section across all platforms.
  const uiux = estimationValue.ui_ux;
  if (uiux && typeof uiux === 'object') {
    const screens = [];
    for (const feats of Object.values(uiux)) screens.push(...normalizeScopeFeatures(feats));
    if (screens.length > 0) {
      sections.push({
        title: 'UI/UX',
        modules: [
          {
            title: 'Screens',
            hours: screens.reduce((sum, f) => sum + f.hours, 0),
            groups: [{ label: null, features: screens }],
          },
        ],
      });
    }
  }
  return sections;
}

// Renders a normalised Detailed Scope (from buildDetailedScopeFromValue) — used by both
// the per-phase blocks and the single (non-phased) estimation so the scope always comes
// from structured estimation_value instead of fragile markdown parsing.
function DetailedScopeView({ scope }) {
  if (!Array.isArray(scope) || scope.length === 0) return null;
  return (
    <div className="estimation-detailed-section">
      <h3 className="estimation-section-title">
        <span className="section-icon">🛠️</span>
        Detailed Scope
      </h3>
      {scope.map((section, sIdx) => (
        <div key={section.title || `scope-section-${sIdx}`} className="estimation-role-section">
          {section.title && <h4 className="role-name-simple">{section.title}</h4>}
          {section.modules.map((mod, mIdx) => (
            <div key={`${mod.title}-${mIdx}`} className="estimation-module-numbered">
              <h5 className="module-name-numbered">
                {mIdx + 1}. {mod.title} — {mod.hours} hrs
              </h5>
              {mod.groups.map((group, gIdx) => (
                <div key={gIdx} className="layer-section-numbered">
                  {group.label && (
                    <div className="layer-label-numbered app-layer-label">{group.label}:</div>
                  )}
                  <ul className="features-list-numbered">
                    {group.features.map((feature, fIdx) => (
                      <li key={fIdx} className="feature-item-numbered">
                        {feature.name} — {feature.hours} hrs
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// New estimation content component for Project Estimation & Scope of Work format
function NewEstimationContent({
  content,
  estimationValue,
  featureOptions,
  estimationOptions,
  qaHoursFromApi,
  estimationSummary,
  totalHoursDict,
  moduleEstimatedHours,
  phases,
  sessionId,
  onDashboardSave,
  onDashboardExport,
  onDashboardRating,
  dashboardOwnerId,
  persistedEstimationIdRef,
  archiveMessages,
  chatSlot,
}) {
  const [csvDownloadSuccessOpen, setCsvDownloadSuccessOpen] = useState(false);
  const [dbSaveSuccessOpen, setDbSaveSuccessOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [estimationReviewOpen, setEstimationReviewOpen] = useState(false);
  const [estimationReviewRating, setEstimationReviewRating] = useState(0);
  const [estimationReviewHover, setEstimationReviewHover] = useState(0);
  const [estimationReviewComment, setEstimationReviewComment] = useState('');
  const { showToast } = useToast();
  const estimationReviewBadgeGradId = `est-review-badge-${useId().replace(/:/g, '')}`;

  const closeCsvDownloadSuccessModal = () => setCsvDownloadSuccessOpen(false);
  const closeDbSaveSuccessModal = () => setDbSaveSuccessOpen(false);

  const closeEstimationReview = () => {
    setEstimationReviewOpen(false);
    setEstimationReviewHover(0);
  };

  const handleEstimationReviewSubmit = () => {
    if (estimationReviewRating < 1) return;
    onDashboardRating?.({
      title: stripMarkdownBoldMarkers(project) || 'Current estimation',
      ratingStars: estimationReviewRating,
      meta: [platform, estimationReviewComment.trim()].filter(Boolean).join(' • ') || undefined,
    });
    closeEstimationReview();
    setEstimationReviewRating(0);
    setEstimationReviewComment('');
  };

  // Parse the new formatted text format (with "Detailed Scope" and numbered modules)
  const parseFormattedTextFormat = (text) => {
    const lines = text.split('\n');
    let project = '';
    let platform = '';
    const modulesByRole = {}; // Track modules by role for Key Modules section
    const detailedScope = {}; // { role: { module: { layer: [features] } } }
    let currentRole = null;
    let currentModule = null;
    let currentLayer = null;
    let inKeyModulesSection = false;
    let inDetailedScope = false;
    let currentRoleInModules = null;

    for (let i = 0; i < lines.length; i++) {
      const originalLine = lines[i];
      const line = originalLine.trim();
      if (!line) continue;

      // Extract project (with or without markdown bold)
      const projectMatch = line.match(/\*\*Project:\*\*\s*(.+)/i) || line.match(/^Project:\s*(.+)/i);
      if (projectMatch) {
        project = projectMatch[1].trim();
        continue;
      }

      // Extract platform (with or without markdown bold)
      const platformMatch = line.match(/\*\*Platform:\*\*\s*(.+)/i) || line.match(/^Platform:\s*(.+)/i);
      if (platformMatch) {
        platform = platformMatch[1].trim();
        continue;
      }

      // Detect "📦 Key Modules" section
      if (line.includes('📦') && line.includes('Key Modules')) {
        inKeyModulesSection = true;
        inDetailedScope = false;
        continue;
      }

      // Parse Key Modules section
      if (inKeyModulesSection && !inDetailedScope) {
        // Role name (standalone line, not a numbered module, not a layer label)
        const isStandaloneLine = originalLine.trim() === originalLine;
        const isNotNumberedModule = !line.match(/^\d+\.\s+/);
        const isNotLayerLabel = !line.endsWith(':');
        const isNotEmpty = line.length > 0;
        const isNotHeader = line !== 'Key Modules' && !line.includes('📦');

        if (isStandaloneLine && isNotNumberedModule && isNotLayerLabel && isNotEmpty && isNotHeader) {
          // Check if next non-empty line is a bullet point (module)
          let nextNonEmptyLine = null;
          for (let j = i + 1; j < lines.length; j++) {
            const nextLine = lines[j].trim();
            if (nextLine) {
              nextNonEmptyLine = nextLine;
              break;
            }
          }
          if (nextNonEmptyLine && nextNonEmptyLine.startsWith('•')) {
            // This is a role in Key Modules section
            currentRoleInModules = line;
            if (!modulesByRole[currentRoleInModules]) {
              modulesByRole[currentRoleInModules] = [];
            }
            continue;
          }
        }

        // Module with bullet in Key Modules section
        if (line.startsWith('•') && currentRoleInModules) {
          const moduleName = line.replace(/^•\s*/, '').trim();
          if (moduleName) {
            modulesByRole[currentRoleInModules].push(moduleName);
          }
          continue;
        }

        // If we hit "Detailed Scope" or "🛠️", switch to detailed scope parsing
        if (line === 'Detailed Scope' || line === '🛠️ Detailed Scope' || (line.includes('🛠️') && line.includes('Detailed Scope'))) {
          inKeyModulesSection = false;
          inDetailedScope = true;
          continue;
        }
      }

      // Detect "Detailed Scope" section (with or without emoji)
      if (line === 'Detailed Scope' || line === '🛠️ Detailed Scope' || (line.includes('🛠️') && line.includes('Detailed Scope'))) {
        inKeyModulesSection = false;
        inDetailedScope = true;
        continue;
      }

      if (!inDetailedScope) continue;

      // Role name detection - standalone line (no leading spaces, not a numbered module, not a layer)
      // A role is a line that:
      // 1. Has no leading spaces (originalLine.trim() === originalLine)
      // 2. Is not a numbered module (doesn't start with "1. ", "2. ", etc.)
      // 3. Is not a layer label (doesn't end with ":")
      // 4. Is not empty
      // 5. Is not "Detailed Scope" or header lines
      // 6. Is followed by a numbered module (1. Module Name) - can have empty lines in between
      const hasNoLeadingSpaces = originalLine.trim() === originalLine && originalLine === line;
      const isNotNumberedModule = !line.match(/^\d+\.\s+/);
      const isNotLayerLabel = !line.endsWith(':');
      const isNotEmpty = line.length > 0;
      const isNotHeader = line !== 'Detailed Scope' &&
        !line.includes('Project:') &&
        !line.includes('Platform:') &&
        line !== 'Project Estimation & Scope of Work' &&
        !line.startsWith('**Project:**') &&
        !line.startsWith('**Platform:**');

      if (hasNoLeadingSpaces && isNotNumberedModule && isNotLayerLabel && isNotEmpty && isNotHeader) {
        // Check if next non-empty line is a numbered module (1. Module Name)
        // Skip empty lines between role and module
        let nextNonEmptyLine = null;
        for (let j = i + 1; j < lines.length; j++) {
          const nextLine = lines[j].trim();
          if (nextLine) {
            nextNonEmptyLine = nextLine;
            break;
          }
        }

        // If next non-empty line is a numbered module, this is a role
        if (nextNonEmptyLine && nextNonEmptyLine.match(/^\d+\.\s+/)) {
          currentRole = line;
          if (!detailedScope[currentRole]) {
            detailedScope[currentRole] = {};
          }
          currentModule = null;
          currentLayer = null;
          continue;
        }
      }

      // Numbered module: "1. Module Name" (no leading spaces)
      const moduleMatch = line.match(/^\d+\.\s+(.+)$/);
      if (moduleMatch && currentRole && originalLine.trim() === line) {
        currentModule = moduleMatch[1].trim();
        if (!detailedScope[currentRole][currentModule]) {
          detailedScope[currentRole][currentModule] = {};
        }
        currentLayer = null;
        continue;
      }

      // Layer label: "   Frontend:", "   **Frontend:**", "### Development (app type)", "### UI/UX", etc.
      const layerMatchH3 = line.match(/^###\s+(Development \(Power Apps\)|Development \(app type\)|Development|UI\/UX)\s*$/);
      if (layerMatchH3 && currentRole && currentModule) {
        currentLayer = (/^Development/.test(layerMatchH3[1])) ? 'database' : 'ui_ux';
        if (!detailedScope[currentRole][currentModule][currentLayer]) {
          detailedScope[currentRole][currentModule][currentLayer] = [];
        }
        continue;
      }
      const layerMatch = originalLine.match(/^\s{3}\*{0,2}(Frontend:|Backend\/API:|Admin Panel:|UI\/UX:|Development \(Power Apps\):|Development \(app type\):|Development:)\*{0,2}$/);
      const layerMatchItalic = originalLine.match(/^\s{3}_?(Frontend:|Backend\/API:|Admin Panel:|UI\/UX:|Development \(Power Apps\):|Development \(app type\):|Development:)_?$/);
      const layerMatchFinal = layerMatch || layerMatchItalic;
      if (layerMatchFinal && currentRole && currentModule) {
        const layerLabel = layerMatchFinal[1].replace(':', '').trim();
        // Map layer labels to keys (Power Apps: Development (app type) -> database; others: Development -> database)
        const layerKeyMap = {
          'Frontend': 'frontend',
          'Backend/API': 'core_and_admin_api',
          'Admin Panel': 'admin_panel_frontend',
          'UI/UX': 'ui_ux',
          'Development (Power Apps)': 'database',
          'Development (app type)': 'database',
          'Development': 'database'
        };
        currentLayer = layerKeyMap[layerLabel] || layerLabel.toLowerCase().replace(/\//g, '_');
        if (!detailedScope[currentRole][currentModule][currentLayer]) {
          detailedScope[currentRole][currentModule][currentLayer] = [];
        }
        continue;
      }

      // Feature with bullet: "      • Feature Name" (6 spaces indentation)
      const featureMatch = originalLine.match(/^\s{6}•\s+(.+)$/);
      if (featureMatch && currentRole && currentModule && currentLayer) {
        const featureName = featureMatch[1].trim();
        if (featureName) {
          detailedScope[currentRole][currentModule][currentLayer].push(featureName);
        }
        continue;
      }
    }

    return { project, platform, modulesByRole, detailedScope, isFormattedText: true };
  };

  const parseContent = (text) => {
    // Check if it's the new formatted text format (with "Detailed Scope" and numbered modules)
    const isFormattedTextFormat = text.includes('Detailed Scope') &&
      (text.match(/^\d+\.\s+\w+/m) || text.includes('Frontend:') || text.includes('Backend/API:') || text.includes('Development:') || text.includes('Development (app type)'));

    if (isFormattedTextFormat) {
      return parseFormattedTextFormat(text);
    }

    // Original parsing logic for old format
    const lines = text.split('\n').map(l => l.trim());

    let project = '';
    let platform = '';
    const modulesByRole = {}; // Changed to object to group by role
    const detailedScope = {};
    let currentRole = null;
    let currentModule = null;

    let inModulesSection = false;
    let inDetailedSection = false;
    let currentRoleInModules = null; // Track current role in modules section

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Skip empty lines
      if (!line) continue;

      // Extract project (new format: "Project: ...")
      const projectMatch = line.match(/^Project:\s*(.+)/i);
      if (projectMatch) {
        project = projectMatch[1].trim();
        continue;
      }

      // Extract platform (new format: "Platform: ...")
      const platformMatch = line.match(/^Platform:\s*(.+)/i);
      if (platformMatch) {
        platform = platformMatch[1].trim();
        continue;
      }

      // Detect Key Modules section (new format: "📦 Key Modules" on same line or separate)
      if (line === '📦' || line.includes('📦')) {
        inModulesSection = true;
        inDetailedSection = false;
        // Check if "Key Modules" is on the same line
        if (line.includes('Key Modules')) {
          continue; // Skip the header line
        }
        continue;
      }

      if (line === 'Key Modules' && inModulesSection) {
        continue; // Skip the header line
      }

      // Also handle "📦 Key Modules" on same line
      if (line.includes('📦') && line.includes('Key Modules')) {
        inModulesSection = true;
        inDetailedSection = false;
        continue;
      }

      // Detect Detailed App Layers section (new format: "🛠️" on one line, "Detailed App Layers per Module" on next)
      if (line === '🛠️' || line.includes('🛠️')) {
        inModulesSection = false;
        inDetailedSection = true;
        continue;
      }

      if (line === 'Detailed App Layers per Module' && inDetailedSection) {
        continue; // Skip the header line
      }

      // Parse modules list (new format: grouped by role)
      // Role name (User, Admin) - standalone line in modules section
      if (inModulesSection && (line === 'User' || line === 'Admin')) {
        // This is a role header in the modules section
        currentRoleInModules = line;
        if (!modulesByRole[currentRoleInModules]) {
          modulesByRole[currentRoleInModules] = [];
        }
        continue;
      }

      // Module name in modules section (after role header)
      if (inModulesSection && line.startsWith('•')) {
        const moduleName = line.replace(/^•\s*/, '').trim();
        if (moduleName && !moduleName.startsWith('_')) {
          if (currentRoleInModules) {
            modulesByRole[currentRoleInModules].push(moduleName);
          }
        }
        continue;
      }

      // Also handle modules without bullet (if format changes)
      if (inModulesSection && line && !line.includes('📦') && line !== 'Key Modules' && line !== 'User' && line !== 'Admin') {
        // Check if it's a module name (not a role, not empty, not a header)
        if (currentRoleInModules && !line.match(/^[A-Z][a-z]+$/)) { // Not just a single word that looks like a role
          // This might be a module name
          if (!modulesByRole[currentRoleInModules].includes(line)) {
            modulesByRole[currentRoleInModules].push(line);
          }
        }
      }

      // Parse detailed scope (new format: Role -> ▶ Module -> • Features)
      if (inDetailedSection) {
        // Role name (User, Admin) - standalone line
        if (line === 'User' || line === 'Admin') {
          currentRole = line;
          if (!detailedScope[currentRole]) {
            detailedScope[currentRole] = {};
          }
          currentModule = null;
          continue;
        }

        // Module with ▶ icon: "▶ Module Name"
        const moduleMatch = line.match(/^▶\s*(.+)$/);
        if (moduleMatch) {
          const moduleName = moduleMatch[1].trim();
          if (moduleName && currentRole) {
            currentModule = moduleName;
            if (!detailedScope[currentRole][currentModule]) {
              detailedScope[currentRole][currentModule] = [];
            }
          }
          continue;
        }

        // Feature with bullet and indentation: "    • Feature Name"
        const featureMatch = line.match(/^\s{4}•\s*(.+)$/);
        if (featureMatch && currentRole && currentModule) {
          const featureName = featureMatch[1].trim();
          if (featureName && featureName !== '_No features defined_') {
            if (!detailedScope[currentRole][currentModule]) {
              detailedScope[currentRole][currentModule] = [];
            }
            detailedScope[currentRole][currentModule].push(featureName);
          }
          continue;
        }
      }
    }

    return { project, platform, modulesByRole, detailedScope };
  };

  const parsed = parseContent(content);
  const { project, platform, modulesByRole, detailedScope, isFormattedText } = parsed;

  // Prefer API estimation_summary / total_hours_dict; then estimation_value; else parse ai_reply.
  let { appLayerTotals, moduleTotals } = resolveEstimationSummaryTotals({
    estimationSummary,
    totalHoursDict,
    estimationValue,
    platform: platform || '',
    qaHoursFromApi,
    fallbackContent: content,
    parseFallback: parseEstimationSummarySections,
  });

  // Override moduleTotals with backend module_estimated_hours when available.
  // This includes UI/UX and all proper per-module totals directly from the backend.
  if (moduleEstimatedHours && typeof moduleEstimatedHours === 'object') {
    const entries = Object.entries(moduleEstimatedHours);
    if (entries.length > 0) {
      moduleTotals = entries.map(([name, hours]) => ({ name, hours: Number(hours) || 0 }));
    }
  }
  if (qaHoursFromApi != null && typeof qaHoursFromApi === 'number') {
    appLayerTotals = appLayerTotals.map((item) =>
      item.label === 'QA' ? { ...item, hours: Math.round(qaHoursFromApi) } : item
    );
  }
  // App layer summary: Power Apps database row label → "Development (Power Apps)" (parsed text may say Development / Development (app type))
  if (isPowerAppsPlatform(platform) && !estimationValue && !estimationSummary) {
    const dbFromText = new Set(['Development', 'Development (app type)']);
    appLayerTotals = appLayerTotals.map((item) => {
      const l = String(item.label).replace(/\*\*/g, '').trim();
      if (dbFromText.has(l)) return { ...item, label: 'Development (Power Apps)' };
      return item;
    });
  }
  appLayerTotals = appendTotalHoursRow(appLayerTotals);

  // Per-phase summary/modules: backend sends a `phases` array when the BRD splits
  // into multiple phases. Each phase carries its own summary/modules so we can show
  // a separate "Estimation Summary" + "Modules" block per phase instead of one
  // combined block.
  const computeTotalsForFields = (fields) => {
    const phaseQa = typeof fields?.qa_hours === 'number' ? fields.qa_hours : null;
    let res = resolveEstimationSummaryTotals({
      estimationSummary: fields?.estimation_summary ?? null,
      totalHoursDict: fields?.total_hours_dict ?? null,
      estimationValue: fields?.estimation_value ?? null,
      platform: platform || '',
      qaHoursFromApi: phaseQa,
      fallbackContent: '',
      parseFallback: null,
    });
    let layerTotals = res.appLayerTotals;
    let modTotals = res.moduleTotals;
    const meh = fields?.module_estimated_hours;
    if (meh && typeof meh === 'object') {
      const entries = Object.entries(meh);
      if (entries.length > 0) {
        modTotals = entries.map(([name, hours]) => ({ name, hours: Number(hours) || 0 }));
      }
    }
    if (phaseQa != null) {
      layerTotals = layerTotals.map((item) =>
        item.label === 'QA' ? { ...item, hours: Math.round(phaseQa) } : item
      );
    }
    return { appLayerTotals: appendTotalHoursRow(layerTotals), moduleTotals: modTotals };
  };

  const phaseBlocks = Array.isArray(phases) && phases.length > 0
    ? phases.map((phaseData, idx) => ({
        key: phaseData?.phase ?? idx,
        label:
          (phaseData?.phase_label && String(phaseData.phase_label).trim()) ||
          `Phase ${phaseData?.phase ?? idx + 1}`,
        detailedScope: buildDetailedScopeFromValue(
          phaseData?.estimation_value,
          phaseData?.feature_options ?? featureOptions
        ),
        ...computeTotalsForFields(phaseData),
      }))
    : [];
  const hasPhaseBlocks = phaseBlocks.length > 0;
  // Sum each phase's own "Total Hours" row (already computed per-phase above) —
  // never recomputed from a merged/flattened scope, so phases can't bleed into
  // each other's numbers here.
  const allPhasesTotalHours = phaseBlocks.reduce((sum, block) => {
    const row = block.appLayerTotals.find(
      (item) => String(item?.label || '').trim().toLowerCase() === 'total hours'
    );
    return sum + (row ? Number(row.hours) || 0 : 0);
  }, 0);

  // Single (non-phased) Detailed Scope built from structured estimation_value so it stays
  // correct after add/delete edits — no fragile markdown re-parsing or raw "(llm)" leakage.
  const combinedDetailedScope = buildDetailedScopeFromValue(estimationValue, featureOptions);
  const hasCombinedDetailedScope = combinedDetailedScope.length > 0;

  // Cross-platform tech-stack options: the backend sends an `estimation_options`
  // array when the BRD offers a choice of build stacks (e.g. Power Apps vs a
  // custom React/Node web app). Each option is a complete, standalone estimation,
  // rendered as its own labelled block (summary + modules + detailed scope).
  const optionBlocks = Array.isArray(estimationOptions) && estimationOptions.length > 0
    ? estimationOptions.map((opt, idx) => {
        const label = (opt?.label && String(opt.label).trim()) || `Option ${idx + 1}`;
        const heading = (opt?.tech_summary && String(opt.tech_summary).trim())
          || (opt?.platform && String(opt.platform).trim())
          || '';
        return {
          key: idx,
          label,
          heading,
          totalHours: typeof opt?.total_hours === 'number' ? opt.total_hours : null,
          detailedScope: buildDetailedScopeFromValue(
            opt?.estimation_value ?? null,
            opt?.feature_options ?? null
          ),
          ...computeTotalsForFields(opt),
        };
      })
    : [];
  const hasOptionBlocks = optionBlocks.length > 0;

  // Check if parsing succeeded - if detailedScope is empty but content has "Detailed Scope", show raw content
  const hasDetailedScope = Object.keys(detailedScope).length > 0;
  const shouldShowRaw = content.includes('Detailed Scope') && !hasDetailedScope && isFormattedText;

  // Strip duplicate block (title + Project + Platform) that appears before "🛠️ Detailed Scope" so we only show it once in the header
  const contentWithoutDuplicateHeader = (() => {
    const idx = content.search(/🛠️\s*\*{0,2}\s*Detailed Scope/i);
    return idx >= 0 ? content.slice(idx) : content;
  })();

  // Layer label mapping for detailed scope (Power Apps: database/ui_ux; Office Add-ins: deliverable sections in markdown)
  const isPowerApps = isPowerAppsPlatform(platform);
  const layerLabels = isPowerApps
    ? { database: 'Development:', ui_ux: 'UI/UX:' }
    : {
      frontend: 'Frontend:',
      core_and_admin_api: 'Backend/API:',
      admin_panel_frontend: 'Admin Panel:',
      ui_ux: 'UI/UX:'
    };
  const allowedLayerKeys = isPowerApps ? ['database', 'ui_ux'] : null;

  return (
    <div className="estimation-display new-estimation-format">
      <div className="estimation-header">
        <div className="estimation-header-content">
          <h2>Project Estimation & Scope of Work</h2>
          {(project || platform) && (
            <div className="estimation-project-info">
              {project && <span className="project-name"><strong>Project:</strong> {project}</span>}
              {platform && <span className="platform-name"><strong>Platform:</strong> {platform}</span>}
            </div>
          )}
        </div>
      </div>

      <div className="estimation-body" data-chat-subanchor="estimation" tabIndex={-1}>
        {hasPhaseBlocks ? (
          <>
            {/* 1. Every phase's Estimation Summary, grouped together up top */}
            {phaseBlocks.map((block) => (
              <div key={`summary-${block.key}`} className="estimation-phase-block estimation-phase-summary-block">
                <div className="estimation-phase-header">
                  <h3 className="estimation-phase-title">{block.label}</h3>
                </div>
                {block.appLayerTotals.length > 0 && (
                  <div className="estimation-app-layer-totals">
                    <div className="estimation-summary-header">
                      <h3 className="estimation-section-title">Estimation Summary</h3>
                    </div>
                    <ul className="estimation-layer-totals-list">
                      {block.appLayerTotals.map((item, idx) => (
                        <li key={idx} className="estimation-layer-total-item">
                          <span className="layer-total-label">{item.label}</span>
                          <span className="layer-total-hours">{item.hours} hrs</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}

            {/* 2. Grand total across every phase */}
            <div className="estimation-phase-block estimation-grand-total-block">
              <div className="estimation-module-total-row estimation-grand-total-row">
                <span className="module-total-grand-label">All Phases Total Hours</span>
                <span className="module-total-grand-hours">{allPhasesTotalHours} hrs</span>
              </div>
            </div>

            {/* 3. Phase-wise detailed estimation (modules, features) — same as before */}
            {phaseBlocks.map((block) => (
              <div key={`detail-${block.key}`} className="estimation-phase-block">
                <div className="estimation-phase-header">
                  <h3 className="estimation-phase-title">{block.label}</h3>
                </div>

                {/* Module total hours per phase */}
                {block.moduleTotals.length > 0 && (
                  <div className="estimation-module-totals">
                    <div className="estimation-summary-header">
                      <h3 className="estimation-section-title">Modules</h3>
                    </div>
                    <ol className="estimation-module-totals-list">
                      {block.moduleTotals.map((item, idx) => (
                        <li key={idx} className="estimation-module-total-item">
                          <span className="module-total-name">
                            <span className="module-total-index">{String(idx + 1).padStart(2, '0')}</span>
                            <span>{item.name}</span>
                          </span>
                          <span className="module-total-hours">{item.hours} hrs</span>
                        </li>
                      ))}
                    </ol>
                    <div className="estimation-module-total-row">
                      <span className="module-total-grand-label">Total</span>
                      <span className="module-total-grand-hours">
                        {block.moduleTotals.reduce((sum, item) => sum + (Number(item.hours) || 0), 0)} hrs
                      </span>
                    </div>
                  </div>
                )}

                {/* Detailed Scope per phase (modules → layers → features/screens) */}
                <DetailedScopeView scope={block.detailedScope} />
              </div>
            ))}
          </>
        ) : hasOptionBlocks ? (
          optionBlocks.map((block) => (
            <div key={block.key} className="estimation-option-block estimation-phase-block">
              <div className="estimation-phase-header">
                <h3 className="estimation-phase-title">
                  {block.label}{block.heading ? `: ${block.heading}` : ''}
                  {block.totalHours != null ? ` — ${block.totalHours} hrs` : ''}
                </h3>
              </div>

              {block.appLayerTotals.length > 0 && (
                <div className="estimation-app-layer-totals">
                  <div className="estimation-summary-header">
                    <h3 className="estimation-section-title">Estimation Summary</h3>
                  </div>
                  <ul className="estimation-layer-totals-list">
                    {block.appLayerTotals.map((item, idx) => (
                      <li key={idx} className="estimation-layer-total-item">
                        <span className="layer-total-label">{item.label}</span>
                        <span className="layer-total-hours">{item.hours} hrs</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {block.moduleTotals.length > 0 && (
                <div className="estimation-module-totals">
                  <div className="estimation-summary-header">
                    <h3 className="estimation-section-title">Modules</h3>
                  </div>
                  <ol className="estimation-module-totals-list">
                    {block.moduleTotals.map((item, idx) => (
                      <li key={idx} className="estimation-module-total-item">
                        <span className="module-total-name">
                          <span className="module-total-index">{String(idx + 1).padStart(2, '0')}</span>
                          <span>{item.name}</span>
                        </span>
                        <span className="module-total-hours">{item.hours} hrs</span>
                      </li>
                    ))}
                  </ol>
                  <div className="estimation-module-total-row">
                    <span className="module-total-grand-label">Total</span>
                    <span className="module-total-grand-hours">
                      {block.moduleTotals.reduce((sum, item) => sum + (Number(item.hours) || 0), 0)} hrs
                    </span>
                  </div>
                </div>
              )}

              <DetailedScopeView scope={block.detailedScope} />
            </div>
          ))
        ) : (
          <>
            {/* App layer total hours (Frontend, Backend/API, Admin Panel, UI/UX) */}
            {appLayerTotals.length > 0 && (
              <div className="estimation-app-layer-totals">
                <div className="estimation-summary-header">
                  <h3 className="estimation-section-title">Estimation Summary</h3>
                </div>
                <ul className="estimation-layer-totals-list">
                  {appLayerTotals.map((item, idx) => (
                    <li key={idx} className="estimation-layer-total-item">
                      <span className="layer-total-label">{item.label}</span>
                      <span className="layer-total-hours">{item.hours} hrs</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Module total hours (1. Module — X hrs) */}
            {moduleTotals.length > 0 && (
              <div className="estimation-module-totals">
                <div className="estimation-summary-header">
                  <h3 className="estimation-section-title">Modules</h3>
                </div>
                <ol className="estimation-module-totals-list">
                  {moduleTotals.map((item, idx) => (
                    <li key={idx} className="estimation-module-total-item">
                      <span className="module-total-name">
                        <span className="module-total-index">{String(idx + 1).padStart(2, '0')}</span>
                        <span>{item.name}</span>
                      </span>
                      <span className="module-total-hours">{item.hours} hrs</span>
                    </li>
                  ))}
                </ol>
                <div className="estimation-module-total-row">
                  <span className="module-total-grand-label">Total</span>
                  <span className="module-total-grand-hours">
                    {moduleTotals.reduce((sum, item) => sum + (Number(item.hours) || 0), 0)} hrs
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Detailed Scope (single estimation). Prefer structured estimation_value so
            add/delete edits always render cleanly; fall back to markdown parsing only
            when no structured value exists. Skipped when phases are present (rendered above). */}
        {!hasPhaseBlocks && !hasOptionBlocks && (hasCombinedDetailedScope ? (
          <DetailedScopeView scope={combinedDetailedScope} />
        ) : shouldShowRaw ? (
          <div className="estimation-detailed-section">
            <div className="message-content">
              <Markdown remarkPlugins={[remarkGfm]}>
                {contentWithoutDuplicateHeader}
              </Markdown>
            </div>
          </div>
        ) : isFormattedText ? (
          <>
            {/* Key Modules Section */}
            {Object.keys(modulesByRole).length > 0 && (
              <div className="estimation-modules-section">
                <h3 className="estimation-section-title">
                  <span className="section-icon">📦</span>
                  Key Modules
                </h3>
                {Object.entries(modulesByRole).map(([roleName, modules]) => (
                  <div key={roleName} className="modules-by-role">
                    <h4 className="role-name-in-modules">{roleName}</h4>
                    <ul className="modules-list-simple">
                      {modules.map((module, idx) => (
                        <li key={idx} className="module-item-simple">{module}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Detailed Scope Section */}
            <div className="estimation-detailed-section">
              {Object.entries(detailedScope).map(([roleName, modules]) => {
                // Filter out modules with no features and get valid modules
                const validModules = Object.entries(modules).filter(([moduleName, layers]) => {
                  return Object.values(layers).some(features =>
                    Array.isArray(features) && features.length > 0
                  );
                });

                // Only render role section if there are valid modules
                if (validModules.length === 0) return null;

                let moduleNum = 1;
                return (
                  <div key={roleName} className="estimation-role-section">
                    <h4 className="role-name-simple">{roleName}</h4>

                    {validModules.map(([moduleName, layers]) => {
                      const currentModuleNum = moduleNum++;
                      return (
                        <div key={moduleName} className="estimation-module-numbered">
                          <h5 className="module-name-numbered">{currentModuleNum}. {moduleName}</h5>

                          {(allowedLayerKeys ? Object.entries(layers).filter(([layerKey]) => allowedLayerKeys.includes(layerKey)) : Object.entries(layers)).map(([layerKey, features]) => {
                            if (!Array.isArray(features) || features.length === 0) return null;
                            return (
                              <div key={layerKey} className="layer-section-numbered">
                                <div className="layer-label-numbered app-layer-label">{layerLabels[layerKey] || layerKey}</div>
                                <ul className="features-list-numbered">
                                  {features.map((feature, idx) => (
                                    <li key={idx} className="feature-item-numbered">
                                      {feature}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {/* Key Modules Section */}
            {Object.keys(modulesByRole).length > 0 && (
              <div className="estimation-modules-section">
                <h3 className="estimation-section-title">
                  <span className="section-icon">📦</span>
                  Key Modules
                </h3>
                {Object.entries(modulesByRole).map(([roleName, modules]) => (
                  <div key={roleName} className="modules-by-role">
                    <h4 className="role-name-in-modules">{roleName}</h4>
                    <ul className="modules-list-simple">
                      {modules.map((module, idx) => (
                        <li key={idx} className="module-item-simple">{module}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Detailed App Layers Section */}
            {Object.keys(detailedScope).length > 0 && (
              <div className="estimation-detailed-section">
                <h3 className="estimation-section-title">
                  <span className="section-icon">🛠️</span>
                  Detailed App Layers per Module
                </h3>

                {Object.entries(detailedScope).map(([roleName, modules]) => (
                  <div key={roleName} className="estimation-role-section">
                    <h4 className="role-name-simple">{roleName}</h4>

                    {Object.entries(modules).map(([moduleName, features]) => (
                      <div key={moduleName} className="estimation-module-simple">
                        <div className="module-name-with-icon">
                          <span className="module-icon">▶</span>
                          <span className="module-name-simple">{moduleName}</span>
                        </div>

                        {Array.isArray(features) && features.length > 0 ? (
                          <ul className="features-list-indented">
                            {features.map((feature, idx) => (
                              <li key={idx} className="feature-item-indented">
                                <span className="feature-bullet">•</span>
                                <span className="feature-name">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="no-features">• _No features defined_</div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        ))}
      </div>

      <div className="estimation-hint">
        <span className="hint-text">You can ask questions about this estimation anytime!</span>
      </div>

      {estimationValue && Object.keys(estimationValue).some(layer => {
        const modules = estimationValue[layer];
        return modules && typeof modules === 'object' && Object.values(modules).some(feats => Array.isArray(feats) && feats.length > 0);
      }) && (
          <div className="estimation-download-row">
            <button
              type="button"
              className="estimation-action-btn estimation-action-btn--secondary"
              disabled={isSaving}
              onClick={async () => {
                if (!sessionId) {
                  showToast('Session not found. Please start again and regenerate estimation.');
                  return;
                }
                setIsSaving(true);
                try {
                  const res = await saveEstimationItems(sessionId, {
                    estimationId: persistedEstimationIdRef?.current ?? null,
                    ownerId: dashboardOwnerId || undefined,
                    estimationValue: estimationValue ?? null,
                    projectName: project || null,
                    platform: platform || null,
                    chatSnapshot: {
                      messages: sanitizeArchiveMessages(Array.isArray(archiveMessages) ? archiveMessages : []),
                      slot: chatSlot ?? null,
                      sessionId,
                    },
                  });
                  const saved = res?.data ?? res;
                  const eid = saved?.estimation_id ?? saved?.data?.estimation_id;
                  if (eid != null && persistedEstimationIdRef) {
                    persistedEstimationIdRef.current = Number(eid);
                  }
                  await onDashboardSave?.({
                    title: project || 'Untitled estimation',
                    meta: platform ? `${platform} • Saved snapshot` : 'Saved snapshot',
                  });
                  setDbSaveSuccessOpen(true);
                } catch (err) {
                  console.error('Failed to save estimation items:', err);
                  showToast('Save failed. Please try again.');
                } finally {
                  setIsSaving(false);
                }
              }}
            >
              <span className="estimation-action-btn-icon" aria-hidden>{isSaving ? '◌' : '✦'}</span>
              <span className="estimation-action-btn-label">{isSaving ? 'Saving snapshot' : 'Save snapshot'}</span>
            </button>
            <button
              type="button"
              className="estimation-action-btn estimation-action-btn--primary"
              onClick={() => {
                const csv = generateEstimationCsv(estimationValue, project, platform);
                const safeName = (project || 'estimation').replace(/[^a-zA-Z0-9_\-\s]/g, '').replace(/\s+/g, '_').trim() || 'estimation';
                downloadCsv(csv, `${safeName}_estimation.csv`);
                onDashboardExport?.({
                  title: stripMarkdownBoldMarkers(project) || 'Export',
                  fileName: `${safeName}_estimation.csv`,
                });
                setCsvDownloadSuccessOpen(true);
              }}
            >
              <span className="estimation-action-btn-icon" aria-hidden>⇣</span>
              <span className="estimation-action-btn-label">Download CSV</span>
            </button>
            <button
              type="button"
              className="estimation-action-btn estimation-action-btn--primary"
              onClick={() => setEstimationReviewOpen(true)}
            >
              <span className="estimation-action-btn-icon" aria-hidden>★</span>
              <span className="estimation-action-btn-label">Rate this Estimation</span>
            </button>
          </div>
        )}

      {csvDownloadSuccessOpen &&
        createPortal(
          <div
            className="estimation-csv-success-overlay"
            onClick={closeCsvDownloadSuccessModal}
            role="presentation"
          >
            <div
              className="estimation-csv-success-modal estimation-csv-success-modal-theme"
              role="dialog"
              aria-modal="true"
              aria-labelledby="estimation-csv-success-title"
              aria-describedby="estimation-csv-success-message"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="estimation-csv-success-close"
                onClick={closeCsvDownloadSuccessModal}
                aria-label="Close"
              >
                ×
              </button>
              <div className="estimation-csv-success-hero" aria-hidden>
                <div className="estimation-csv-success-confetti">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <span key={i} className="estimation-csv-success-confetti-bit" />
                  ))}
                </div>
                <div className="estimation-csv-success-icon-ring">
                  <svg
                    className="estimation-csv-success-check"
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <h2 id="estimation-csv-success-title" className="estimation-csv-success-title">
                Export ready
              </h2>
              <p id="estimation-csv-success-message" className="estimation-csv-success-body">
                Your estimation report has been exported as a CSV file and is ready to share.
              </p>
              <button
                type="button"
                className="estimation-csv-success-ok"
                onClick={closeCsvDownloadSuccessModal}
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )}

      {dbSaveSuccessOpen &&
        createPortal(
          <div
            className="estimation-csv-success-overlay"
            onClick={closeDbSaveSuccessModal}
            role="presentation"
          >
            <div
              className="estimation-csv-success-modal estimation-csv-success-modal-theme estimation-db-save-success-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="estimation-db-save-success-title"
              aria-describedby="estimation-db-save-success-message"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="estimation-csv-success-close"
                onClick={closeDbSaveSuccessModal}
                aria-label="Close"
              >
                ×
              </button>

              <div className="estimation-db-save-success-badge" aria-hidden>
                <svg
                  className="estimation-db-save-success-check"
                  width="44"
                  height="44"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="currentColor"
                    strokeWidth="2.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <h2 id="estimation-db-save-success-title" className="estimation-csv-success-title">
                Estimation saved
              </h2>
              <p id="estimation-db-save-success-message" className="estimation-csv-success-body">
                The current estimation snapshot has been stored successfully.
              </p>
              <button
                type="button"
                className="estimation-csv-success-ok"
                onClick={closeDbSaveSuccessModal}
              >
                CLOSE
              </button>
            </div>
          </div>,
          document.body
        )}

      {estimationReviewOpen &&
        createPortal(
          <div
            className="estimation-review-overlay"
            onClick={closeEstimationReview}
            role="presentation"
          >
            <div
              className="estimation-csv-success-modal estimation-csv-success-modal-theme estimation-review-card"
              role="dialog"
              aria-modal="true"
              aria-labelledby="estimation-review-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="estimation-csv-success-close estimation-review-close"
                onClick={closeEstimationReview}
                aria-label="Close"
              >
                ×
              </button>

              <div className="estimation-review-wave-header" aria-hidden>
                <div className="estimation-review-wave-tint" />
                <svg
                  className="estimation-review-wave-svg estimation-review-wave-svg--back"
                  viewBox="0 0 1200 100"
                  preserveAspectRatio="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="estimation-review-wave-path"
                    d="M0,55 C200,25 400,85 600,45 C800,15 1000,75 1200,40 L1200,100 L0,100 Z"
                    fill="var(--brand-primary)"
                    fillOpacity="0.14"
                  />
                </svg>
                <svg
                  className="estimation-review-wave-svg estimation-review-wave-svg--front"
                  viewBox="0 0 1200 100"
                  preserveAspectRatio="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="estimation-review-wave-path"
                    d="M0,70 C250,40 450,95 700,50 C900,25 1050,80 1200,55 L1200,100 L0,100 Z"
                    fill="var(--brand-secondary)"
                    fillOpacity="0.12"
                  />
                </svg>
                <div className="estimation-review-wave-badge">
                  <svg
                    className="estimation-review-wave-badge-star"
                    viewBox="0 0 48 48"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <defs>
                      <linearGradient id={estimationReviewBadgeGradId} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="var(--brand-primary)" />
                        <stop offset="100%" stopColor="var(--brand-secondary)" />
                      </linearGradient>
                    </defs>
                    <circle cx="24" cy="24" r="22" fill="var(--bg-secondary)" stroke="var(--border-color)" strokeWidth="1.5" />
                    <path
                      fill={`url(#${estimationReviewBadgeGradId})`}
                      d="M24 14l2.2 4.5 4.9.7-3.5 3.4.8 4.9L24 24.9l-4.4 2.3.8-4.9-3.5-3.4 4.9-.7L24 14z"
                    />
                  </svg>
                </div>
              </div>

              <div className="estimation-review-body">
                <h2 id="estimation-review-title" className="estimation-review-heading">
                  Rate this estimation
                </h2>
                <p className="estimation-review-subtitle">
                  Help improve the scope quality by rating the usefulness of this estimate and adding any quick notes.
                </p>
                <div
                  className="estimation-review-stars"
                  role="group"
                  aria-label="Rate this estimation from 1 to 5 stars"
                >
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = n <= (estimationReviewHover || estimationReviewRating);
                    return (
                      <button
                        key={n}
                        type="button"
                        className={
                          active
                            ? 'estimation-review-star estimation-review-star--active'
                            : 'estimation-review-star'
                        }
                        onMouseEnter={() => setEstimationReviewHover(n)}
                        onMouseLeave={() => setEstimationReviewHover(0)}
                        onClick={() => setEstimationReviewRating(n)}
                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                        aria-pressed={estimationReviewRating >= n}
                      >
                        <svg
                          className="estimation-review-star-shape"
                          viewBox="0 0 24 24"
                          width="36"
                          height="36"
                          aria-hidden
                          focusable="false"
                        >
                          <path
                            className="estimation-review-star-path"
                            d="M12 2.5l2.84 5.76 6.35.92-4.59 4.47 1.08 6.33L12 17.25l-5.68 2.98 1.08-6.33L3 9.18l6.35-.92L12 2.5z"
                            fill={active ? 'currentColor' : 'none'}
                            stroke={active ? 'var(--estimation-review-star-stroke)' : 'currentColor'}
                            strokeWidth={active ? 1.15 : 1.35}
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    );
                  })}
                </div>

                <label htmlFor="estimation-review-comment" className="estimation-review-comment-label">
                  💬 Add comment (Optional)
                </label>
                <textarea
                  id="estimation-review-comment"
                  className="estimation-review-textarea"
                  placeholder="Enter your message"
                  value={estimationReviewComment}
                  onChange={(e) => setEstimationReviewComment(e.target.value)}
                  rows={3}
                />

                <button
                  type="button"
                  className="estimation-review-submit"
                  disabled={estimationReviewRating < 1}
                  onClick={handleEstimationReviewSubmit}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

// Generated Modules and Features content component
function ModulesFeaturesContent({ message, onConfirmScope, structureLocked = false }) {
  const content = message.content;
  const { toastMessage, showToast } = useToast();
  const [addModal, setAddModal] = useState({ open: false, moduleIdx: null, layerIdx: null, anchor: null });
  const [addFeatureInput, setAddFeatureInput] = useState('');
  const addFeatureInputRef = useRef(null);
  const MODAL_WIDTH = 320;
  const MODAL_MIN_EDGE = 12;

  // Parse the content to extract modules and features
  const parseContent = (text) => {
    const modules = [];
    let currentModule = null;
    let currentLayer = null;

    const allLines = text.split('\n');
    let inModulesSection = false;
    let inProjectScopeSection = false;
    const moduleNames = [];

    // Layer name mapping
    const layerNameMap = {
      'Frontend': 'Frontend',
      'Core API and Admin Backend': 'Core API and Admin Backend',
      'Admin Panel Frontend': 'Admin Panel Frontend',
      'UI/UX': 'UI/UX'
    };

    // Known layer headers
    const knownLayerHeaders = [
      'Frontend:',
      'Core API and Admin Backend:',
      'Admin Panel Frontend:',
      'UI/UX:'
    ];

    for (let i = 0; i < allLines.length; i++) {
      const line = allLines[i];
      const trimmed = line.trim();

      // Skip empty lines
      if (!trimmed) continue;

      // Detect sections
      if (trimmed === 'Modules:') {
        inModulesSection = true;
        inProjectScopeSection = false;
        continue;
      }

      if (trimmed === 'Project Scope:') {
        inModulesSection = false;
        inProjectScopeSection = true;
        continue;
      }

      // Skip separator lines (lines with only dashes, like "----------")
      if (trimmed.match(/^-+$/) || trimmed.match(/^=+$/)) {
        continue;
      }

      // Parse Modules: section - extract module names (with or without "- " prefix)
      if (inModulesSection) {
        // Stop when we hit Project Scope: or separator
        if (trimmed === 'Project Scope:' || trimmed.match(/^-+$/)) {
          inModulesSection = false;
          if (trimmed === 'Project Scope:') {
            inProjectScopeSection = true;
          }
          continue;
        }

        // Skip if it's still the "Modules:" header line
        if (trimmed.toLowerCase() === 'modules:') {
          continue;
        }

        let moduleName = trimmed;
        // Handle bullet points
        if (trimmed.startsWith('- ')) {
          moduleName = trimmed.substring(2).trim();
        } else if (trimmed.startsWith('* ')) {
          moduleName = trimmed.substring(2).trim();
        }

        // Only add if it's not a section header, not empty, and looks like a module name
        // Module names are typically capitalized, have multiple words, and don't end with colon
        if (moduleName &&
          moduleName !== 'Modules:' &&
          moduleName !== 'Project Scope:' &&
          moduleName.length > 3 &&
          !moduleName.endsWith(':') &&
          !moduleName.match(/^[a-z]/)) { // Should start with capital letter
          moduleNames.push(moduleName);
        }
        continue;
      }

      // Parse Project Scope: section
      if (inProjectScopeSection) {
        // Check if this is a layer header (ends with colon and matches known layers)
        const isLayerHeader = trimmed.endsWith(':') && knownLayerHeaders.some(header =>
          trimmed === header || trimmed.includes(header.replace(':', ''))
        );

        if (isLayerHeader) {
          // Save previous layer if exists
          if (currentLayer && currentModule) {
            currentModule.layers.push(currentLayer);
          }

          const layerName = trimmed.replace(':', '').trim();
          currentLayer = {
            name: layerNameMap[layerName] || layerName,
            features: []
          };
          continue;
        }

        // Check if this is a feature (starts with dash, may be indented with spaces)
        // Features are indented with "  - " (two spaces) or more
        const featureMatch = trimmed.match(/^\s*-\s*(.+)$/);
        if (featureMatch && currentLayer && currentModule) {
          currentLayer.features.push(featureMatch[1].trim());
          continue;
        }

        // Check if this is a module name (plain text header, not a layer header, not a feature)
        // Module names don't end with colon, don't start with dash, and match names from Modules: section
        // Also exclude lines that start with spaces followed by dash (those are features)
        if (!trimmed.endsWith(':') &&
          !trimmed.match(/^\s*-\s/) && // Not starting with dash (with or without spaces)
          trimmed.length > 3) {

          // Check if this matches a module name from the Modules: section
          const foundModuleName = moduleNames.find(name => {
            const nameLower = name.toLowerCase().trim();
            const trimmedLower = trimmed.toLowerCase().trim();
            // Exact match
            if (trimmed === name || trimmedLower === nameLower) return true;
            // Contains match (for longer names)
            if (name.length > 5 && trimmedLower.includes(nameLower)) return true;
            if (name.length > 5 && nameLower.includes(trimmedLower)) return true;
            // Word boundary match
            const nameWords = nameLower.split(/\s+/);
            const trimmedWords = trimmedLower.split(/\s+/);
            if (nameWords.length > 0 && trimmedWords.length > 0) {
              // Check if first word matches
              if (nameWords[0] === trimmedWords[0] && nameWords[0].length > 3) return true;
            }
            return false;
          });

          // If it matches a module name, treat it as a new module
          if (foundModuleName) {
            // Save previous module if exists
            if (currentModule) {
              if (currentLayer) {
                currentModule.layers.push(currentLayer);
                currentLayer = null;
              }
              if (currentModule.layers.length > 0 || currentModule.name) {
                modules.push(currentModule);
              }
            }

            currentModule = {
              name: foundModuleName,
              layers: []
            };
            currentLayer = null;
            continue;
          }

          // If we have module names but no match, and this looks like a module name
          // (capitalized, multiple words, not a feature), use it anyway
          // This handles cases where module names might not match exactly
          if (moduleNames.length > 0 &&
            trimmed.length > 5 &&
            trimmed[0] === trimmed[0].toUpperCase() &&
            trimmed.split(/\s+/).length >= 2) {
            // Check if any module name contains this text or vice versa
            const partialMatch = moduleNames.find(name => {
              const nameLower = name.toLowerCase();
              const trimmedLower = trimmed.toLowerCase();
              return nameLower.includes(trimmedLower) || trimmedLower.includes(nameLower);
            });

            if (partialMatch || !currentModule) {
              // Save previous module if exists
              if (currentModule) {
                if (currentLayer) {
                  currentModule.layers.push(currentLayer);
                  currentLayer = null;
                }
                if (currentModule.layers.length > 0 || currentModule.name) {
                  modules.push(currentModule);
                }
              }

              currentModule = {
                name: partialMatch || trimmed,
                layers: []
              };
              currentLayer = null;
              continue;
            }
          }
        }
      }
    }

    // Add the last module and layer
    if (currentLayer && currentModule) {
      currentModule.layers.push(currentLayer);
    }
    if (currentModule) {
      modules.push(currentModule);
    }

    return { modules, moduleNames };
  };

  const parseResult = parseContent(content);
  const [modules, setModules] = useState(() => parseResult?.modules || []);
  const moduleNames = parseResult?.moduleNames || [];

  const closeAddModal = () => {
    setAddModal((prev) => ({ ...prev, open: false, anchor: null }));
    document.body.style.overflow = '';
  };

  const openAddModal = (moduleIdx, layerIdx, ev) => {
    if (structureLocked) return;
    ev?.preventDefault?.();
    ev?.stopPropagation?.();
    const anchor = ev?.currentTarget ? (() => {
      const rect = ev.currentTarget.getBoundingClientRect();
      let left = rect.right + 12;
      let top = rect.top;
      if (left + MODAL_WIDTH + MODAL_MIN_EDGE > window.innerWidth) left = window.innerWidth - MODAL_WIDTH - MODAL_MIN_EDGE;
      if (left < MODAL_MIN_EDGE) left = MODAL_MIN_EDGE;
      if (top + 220 > window.innerHeight) top = window.innerHeight - 220;
      if (top < MODAL_MIN_EDGE) top = MODAL_MIN_EDGE;
      return { left, top };
    })() : null;
    setAddModal({ open: true, moduleIdx, layerIdx, anchor });
    setAddFeatureInput('');
    document.body.style.overflow = 'hidden';
  };

  useEffect(() => {
    if (addModal.open && addFeatureInputRef.current) {
      const t = requestAnimationFrame(() => {
        addFeatureInputRef.current?.focus({ preventScroll: true });
      });
      return () => cancelAnimationFrame(t);
    }
  }, [addModal.open]);

  const removeFeature = (moduleIdx, layerIdx, featureIdx) => {
    if (structureLocked) return;
    const featureName = modules[moduleIdx]?.layers?.[layerIdx]?.features?.[featureIdx];
    setModules((prev) => prev.map((mod, mIdx) => {
      if (mIdx !== moduleIdx) return mod;
      return {
        ...mod,
        layers: (mod.layers || []).map((layer, lIdx) => {
          if (lIdx !== layerIdx) return layer;
          return {
            ...layer,
            features: (layer.features || []).filter((_, fIdx) => fIdx !== featureIdx),
          };
        }),
      };
    }));
    if (featureName) showToast(`Feature "${featureName}" deleted successfully`);
  };

  const addFeature = (moduleIdx, layerIdx, name) => {
    if (structureLocked) return;
    const trimmed = String(name).trim();
    if (!trimmed) return;
    const existing = modules[moduleIdx]?.layers?.[layerIdx]?.features || [];
    if (existing.some((f) => String(f).trim().toLowerCase() === trimmed.toLowerCase())) {
      showToast('This feature already exists');
      return;
    }
    setModules((prev) => prev.map((mod, mIdx) => {
      if (mIdx !== moduleIdx) return mod;
      return {
        ...mod,
        layers: (mod.layers || []).map((layer, lIdx) => {
          if (lIdx !== layerIdx) return layer;
          return { ...layer, features: [...(layer.features || []), trimmed] };
        }),
      };
    }));
    setAddFeatureInput('');
    closeAddModal();
    showToast(`Feature "${trimmed}" added successfully`);
  };

  const handleConfirm = () => {
    if (structureLocked || !onConfirmScope) return;
    const scope = { User: {} };
    modules.forEach((mod) => {
      scope.User[mod.name] = {};
      (mod.layers || []).forEach((layer) => {
        const key = FEATURE_LAYER_LABEL_TO_KEY[layer.name] || layer.name;
        scope.User[mod.name][key] = [...(layer.features || [])];
      });
    });
    const confirmLabel = (Array.isArray(message.options) ? message.options : []).find((opt) => opt && opt !== 'Cancel') || 'Confirm';
    onConfirmScope(scope, confirmLabel);
  };

  // Header component to avoid duplication
  const header = (
    <div className="modules-features-header">
      <div className="summary-message-icon-wrapper">
        <LayoutIcon />
      </div>
      <h2>Generated Modules and Features</h2>
    </div>
  );

  // If parsing failed or no modules found, show raw content as fallback
  if (!modules || modules.length === 0) {
    return (
      <div className="modules-features-display" data-chat-subanchor="features" tabIndex={-1}>
        {header}
        <div className="message-content">
          <Markdown remarkPlugins={[remarkGfm]}>
            {content}
          </Markdown>
        </div>
      </div>
    );
  }

  return (
    <div className="modules-features-display" data-chat-subanchor="features" tabIndex={-1}>
      {header}

      {/* Modules List Section */}
      {moduleNames && moduleNames.length > 0 && (
        <div className="modules-list-section">
          <h3 className="modules-list-title">Modules</h3>
          <div className="modules-list-grid">
            {moduleNames.map((moduleName, idx) => (
              <div key={idx} className="module-list-item">
                <span className="module-list-number">{idx + 1}</span>
                <span className="module-list-name">{moduleName}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detailed Project Scope Section */}
      <div className="modules-features-body">
        <h3 className="project-scope-title">Project Scope</h3>
        {modules.map((module, moduleIdx) => (
          <div key={moduleIdx} className="module-card">
            <h3 className="module-title">
              <span className="module-number">{moduleIdx + 1}.</span> {module.name}
            </h3>

            {module.layers && module.layers.length > 0 && (
              <div className="module-layers">
                {module.layers.map((layer, layerIdx) => (
                  <div key={layerIdx} className="layer-section">
                    <div className="layer-header">
                      <span className="layer-name">{layer.name}</span>
                      <div className="layer-header-actions">
                        <span className="layer-count">
                          {(layer.features || []).length} {(layer.features || []).length === 1 ? 'feature' : 'features'}
                        </span>
                        {!structureLocked && (
                          <button
                            type="button"
                            className="add-feature-btn"
                            onClick={(e) => openAddModal(moduleIdx, layerIdx, e)}
                            title="Add feature"
                          >
                            +
                          </button>
                        )}
                      </div>
                    </div>

                    <ul className="features-list">
                      {(layer.features || []).map((feature, featureIdx) => (
                        <li key={featureIdx} className={`feature-item ${structureLocked ? '' : 'feature-item-editable'}`}>
                          <span className="feature-item-text">{feature}</span>
                          {!structureLocked && (
                            <button
                              type="button"
                              className="delete-feature-btn"
                              onClick={() => removeFeature(moduleIdx, layerIdx, featureIdx)}
                              title="Delete feature"
                            >
                              <DeleteIcon />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {!structureLocked && onConfirmScope && (
          <div className="editable-scope-actions">
            <button type="button" className="confirm-scope-btn" onClick={handleConfirm}>
              {(Array.isArray(message.options) ? message.options : []).find((opt) => opt && opt !== 'Cancel') || 'Confirm'}
            </button>
          </div>
        )}
      </div>

      {!structureLocked && addModal.open && createPortal(
        <div className="add-feature-modal-overlay" onClick={closeAddModal}>
          <div
            className="add-feature-modal add-feature-modal-theme"
            style={addModal.anchor ? { left: addModal.anchor.left, top: addModal.anchor.top } : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="add-feature-modal-close" onClick={closeAddModal} aria-label="Close">
              ×
            </button>
            <div className="add-feature-modal-header">
              <span className="add-feature-modal-kicker">Scope editor</span>
              <h4 className="add-feature-modal-title">Add feature</h4>
            </div>
            <input
              ref={addFeatureInputRef}
              type="text"
              className="add-feature-modal-input"
              placeholder="Feature name..."
              value={addFeatureInput}
              onChange={(e) => setAddFeatureInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addFeature(addModal.moduleIdx, addModal.layerIdx, addFeatureInput);
              }}
            />
            <button
              type="button"
              className="add-feature-modal-submit"
              onClick={() => addFeature(addModal.moduleIdx, addModal.layerIdx, addFeatureInput)}
              disabled={!addFeatureInput.trim()}
            >
              Add Feature
            </button>
          </div>
        </div>,
        document.body
      )}

      {toastMessage && createPortal(
        <div className="module-toast">
          <div className="module-toast-content">
            <span className="module-toast-icon">✓</span>
            <span className="module-toast-message">{toastMessage}</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Helper function to format role name to title case with proper spacing
// Handles camelCase, PascalCase, lowercase, and mixed formats
const formatRoleName = (roleName) => {
  if (!roleName) return roleName;

  // Remove extra spaces
  let formatted = roleName.trim().replace(/\s+/g, ' ');

  // Handle camelCase/PascalCase: insert space before capital letters
  // Example: "CourseDesigner" -> "Course Designer"
  formatted = formatted.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Split by spaces and format each word to title case
  formatted = formatted
    .split(/\s+/)
    .map(word => {
      // Skip empty words
      if (!word) return word;
      // Capitalize first letter, lowercase the rest
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');

  return formatted;
};

// Roles content component (before modules generation) with add/delete functionality
function RolesContent({ message, onOptionClick, messages = [], structureLocked = false }) {
  const content = message.content;
  const slot = message.slot;
  const [newRoleName, setNewRoleName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const { toastMessage, showToast } = useToast();

  // Extract project name from previous messages (look for confirmation summary or project info)
  const getProjectName = () => {
    // First, try to find in current message content
    const projectMatch = content.match(/\*\*Project:\*\*\s*(.+)/i);
    if (projectMatch) {
      return projectMatch[1].trim();
    }

    // Look in previous assistant messages for project info
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === 'assistant') {
        const match = msg.content.match(/\*\*Project:\*\*\s*(.+)/i) ||
          msg.content.match(/Project:\s*(.+)/i) ||
          msg.content.match(/Project\s*:\s*(.+)/i);
        if (match) {
          return match[1].trim();
        }
      }
    }

    return 'Your Project'; // Fallback
  };

  const projectName = getProjectName();

  // Parse the content to extract roles and split into sections
  const parseContent = (text) => {
    const lines = text.split('\n');
    const roles = [];
    let headerSection = '';
    let instructionSection = '';

    // Find header (first line with 👥 and Project Roles)
    let headerIdx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes('👥') && lines[i].includes('Project Roles')) {
        headerSection = lines[i];
        headerIdx = i;
        break;
      }
    }

    // Find footer (line with "Type" and "Continue" or "Click" and "Continue")
    let footerIdx = -1;
    for (let i = lines.length - 1; i >= 0; i--) {
      const line = lines[i].toLowerCase();
      if ((line.includes('type') || line.includes('click')) && line.includes('continue')) {
        footerIdx = i;
        break;
      }
    }

    // Extract instructions (between header and first role)
    if (headerIdx >= 0) {
      const instructionLines = [];
      for (let i = headerIdx + 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith('•')) {
          break; // Hit roles section
        }
        if (line && !line.toLowerCase().includes('type') && !line.toLowerCase().includes('continue')) {
          instructionLines.push(lines[i]);
        }
      }
      instructionSection = instructionLines.join('\n');
    }

    // Extract roles (bullet points between instructions and footer)
    const startIdx = headerIdx >= 0 ? headerIdx + 1 : 0;
    const endIdx = footerIdx >= 0 ? footerIdx : lines.length;

    for (let i = startIdx; i < endIdx; i++) {
      const line = lines[i].trim();
      if (line.startsWith('•')) {
        const roleName = line.replace(/^•\s*/, '').trim();
        const cleanRole = roleName.replace(/\*\*/g, '').replace(/^_|_$/g, '');
        if (cleanRole && cleanRole.toLowerCase() !== 'no roles defined yet') {
          // Format role name to title case with proper spacing
          roles.push(formatRoleName(cleanRole));
        }
      }
    }

    return {
      roles,
      headerSection,
      instructionSection
    };
  };

  const { roles, headerSection, instructionSection } = parseContent(content);

  const handleDeleteRole = async (roleName) => {
    if (structureLocked || isProcessing) return;
    setIsProcessing(true);
    try {
      const deleteCommand = `Delete role "${roleName}"`;
      await onOptionClick(deleteCommand, { explicitSlot: slot, silent: true, updateExisting: true });
      // Show success toast only after backend confirms the operation
      showToast(`Role "${roleName}" deleted successfully`);
    } catch (error) {
      showToast(`Failed to delete role "${roleName}"`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    if (structureLocked || !newRoleName.trim() || isProcessing) {
      return;
    }
    const roleNameToAdd = newRoleName.trim();
    setIsProcessing(true);
    try {
      const addCommand = `Add role "${roleNameToAdd}"`;
      await onOptionClick(addCommand, { explicitSlot: slot, silent: true, updateExisting: true });
      // Show success toast only after backend confirms the operation
      showToast(`Role "${roleNameToAdd}" added successfully`);
      setNewRoleName('');
    } catch (error) {
      showToast(`Failed to add role "${roleNameToAdd}"`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContinue = () => {
    if (structureLocked) return;
    onOptionClick('Continue', { explicitSlot: slot });
  };

  return (
    <div className="roles-display">
      {/* Display backend's exact header */}
      {headerSection && (
        <div className="roles-header-text">
          <Markdown remarkPlugins={[remarkGfm]}>
            {headerSection}
          </Markdown>
        </div>
      )}

      {/* Display backend's instruction section (contains Project info) or custom fallback */}
      {instructionSection ? (
        <div className="roles-instructions">
          <Markdown remarkPlugins={[remarkGfm]}>
            {instructionSection}
          </Markdown>
        </div>
      ) : (
        <div className="roles-instructions">
          <Markdown remarkPlugins={[remarkGfm]}>
            **Project:** {projectName}
          </Markdown>
        </div>
      )}

      {/* Interactive Roles List */}
      <div className="roles-body">
        {roles.length > 0 ? (
          <ul className="roles-list">
            {roles.map((role, idx) => (
              <li key={idx} className="role-item">
                <span className="role-name">• {role}</span>
                {!structureLocked && (
                  <button
                    className="delete-role-btn"
                    onClick={() => handleDeleteRole(role)}
                    disabled={isProcessing}
                    title={`Delete ${role}`}
                  >
                    <DeleteIcon />
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="roles-no-data">_No roles defined yet._</div>
        )}
      </div>

      {/* Add Role Section */}
      {!structureLocked && (
        <div className="add-role-section">
          <h4 className="add-role-title">Add New Role</h4>
          <form onSubmit={handleAddRole} className="add-role-form">
            <div className="role-input-group">
              <input
                type="text"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="Enter role name..."
                className="role-input"
              />
              <button
                type="submit"
                disabled={!newRoleName.trim() || isProcessing}
                className="add-role-btn"
              >
                {isProcessing ? 'Processing...' : 'Add Role'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="roles-deliverable-note">
        <strong>Note:</strong> If you selected <strong>Admin Panel</strong> in your deliverables, an admin role (a role whose name includes &quot;admin&quot;) is required for admin panel feature generation.
      </div>

      {/* Display custom footer text */}
      <div className="roles-footer-text">
        <Markdown remarkPlugins={[remarkGfm]}>
          Click Continue to generate modules for your project.
        </Markdown>
      </div>

      {/* Continue Button */}
      <div className="roles-continue-section">
        <button
          onClick={handleContinue}
          disabled={structureLocked || isProcessing || roles.length === 0}
          className="roles-continue-btn"
        >
          Continue
        </button>
      </div>

      {/* Toast Notification */}
      {toastMessage && createPortal(
        <div className="module-toast">
          <div className="module-toast-content">
            <span className="module-toast-icon">✓</span>
            <span className="module-toast-message">{toastMessage}</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Interactive Role Modules content component with delete/add functionality
function InteractiveRoleModulesContent({ message, onOptionClick, structureLocked = false }) {
  const content = message.content;
  const [newModuleName, setNewModuleName] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const { toastMessage, showToast } = useToast();

  // Parse the content to extract project info, platform, and role modules
  const parseContent = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);

    let project = '';
    let platform = '';
    let showKeyModules = false; // Track if "📦 Key Modules" section should be shown
    const roles = [];
    let currentRole = null;

    for (const line of lines) {
      // Skip header line if present
      if (line.includes('🚀') && line.includes('Project Modules by Role')) {
        continue;
      }

      // Detect "📦 Key Modules" section
      if (line.includes('📦') && line.includes('Key Modules')) {
        showKeyModules = true;
        continue;
      }

      // Skip footer message
      if (line.toLowerCase().includes('you can ask questions')) {
        continue;
      }

      // Extract project
      const projectMatch = line.match(/\*\*Project:\*\*\s*(.+)/i);
      if (projectMatch) {
        project = projectMatch[1].trim();
        continue;
      }

      // Extract platform
      const platformMatch = line.match(/\*\*Platform:\*\*\s*(.+)/i);
      if (platformMatch) {
        platform = platformMatch[1].trim();
        continue;
      }

      // Detect role header: ### 👤 **Role Name**
      const roleMatch = line.match(/###\s*👤\s*\*\*(.+?)\*\*/);
      if (roleMatch) {
        // Save previous role if exists
        if (currentRole) {
          roles.push(currentRole);
        }
        currentRole = {
          name: roleMatch[1].trim(),
          modules: []
        };
        continue;
      }

      // Detect module: • Module Name (handles both regular and italic placeholders)
      const moduleMatch = line.match(/^[•\*]\s*(.+)$/);
      if (moduleMatch && currentRole) {
        let moduleName = moduleMatch[1].trim();
        // Remove markdown formatting (italic underscores)
        moduleName = moduleName.replace(/^_|_$/g, '');
        // Skip placeholder text
        const lowerName = moduleName.toLowerCase();
        if (lowerName !== 'no modules defined' && !lowerName.includes('no modules')) {
          currentRole.modules.push(moduleName);
        }
        continue;
      }
    }

    // Add last role
    if (currentRole) {
      roles.push(currentRole);
    }

    return { project, platform, roles, showKeyModules };
  };

  const { project, platform, roles, showKeyModules } = parseContent(content);

  const displayedRoles =
    selectedRole.length > 0 ? roles.filter((r) => r.name === selectedRole) : roles;

  const handleDeleteModule = async (roleName, moduleName) => {
    if (structureLocked || isProcessing) return;
    setIsProcessing(true);
    try {
      // Send delete command to backend silently (without showing user message)
      const deleteCommand = `Delete module "${moduleName}" from ${roleName} role`;
      // Use onOptionClick with a flag to indicate silent update
      // Backend will return updated module list with slot: "modules.edit"
      await onOptionClick(deleteCommand, {
        silent: true,
        updateExisting: true
      });
      // Show success toast only after backend confirms the operation
      showToast(`Module "${moduleName}" deleted successfully. Features will be regenerated.`);
    } catch (error) {
      console.error('Error deleting module:', error);
      // Show error toast if operation fails
      showToast(`Failed to delete module "${moduleName}". Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddModule = async (e) => {
    e.preventDefault();
    if (structureLocked || !newModuleName.trim() || !selectedRole || isProcessing) {
      return;
    }
    const moduleNameToAdd = newModuleName.trim();
    setIsProcessing(true);
    try {
      const addCommand = `Add module "${moduleNameToAdd}" to ${selectedRole} role`;
      const response = await onOptionClick(addCommand, {
        silent: true,
        updateExisting: true
      });
      const isDuplicate = response?.aiReply && (
        response.aiReply.includes('already exists') ||
        response.aiReply.includes('module already')
      );
      if (isDuplicate) {
        showToast('Module is already present.');
      } else {
        showToast(`Module "${moduleNameToAdd}" added successfully. Features are being generated...`);
      }
      setNewModuleName('');
      setSelectedRole('');
    } catch (error) {
      console.error('Error adding module:', error);
      showToast(`Failed to add module "${moduleNameToAdd}". Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="interactive-role-modules-display interactive-role-modules-display-single-platform">
      <div className="role-modules-header">
        <div className="role-modules-header-title-row">
          <h2>🚀 Project Modules by Role</h2>
          <span className="role-modules-badge">Single-Platform</span>
        </div>
        {(project || platform) && (
          <div className="role-modules-project-info">
            {project && <span className="project-name"><strong>Project:</strong> {project}</span>}
            {platform && <span className="platform-name"><strong>Platform:</strong> {platform}</span>}
          </div>
        )}
      </div>

      {/* Show Key Modules section if present in backend response */}
      {showKeyModules && (
        <div className="key-modules-section">
          <h3 className="key-modules-title">📦 Key Modules</h3>
        </div>
      )}

      <div className="role-modules-body">
        {roles.length > 0 ? (
          displayedRoles.map((role, roleIdx) => (
            <div key={roleIdx} className="role-card">
              <div className="role-header">
                <span className="role-icon">👤</span>
                <h3 className="role-name">{role.name}</h3>
              </div>

              {role.modules.length > 0 ? (
                <ul className="role-modules-list">
                  {role.modules.map((module, moduleIdx) => (
                    <li key={moduleIdx} className="role-module-item">
                      <span className="module-name">{module}</span>
                      {!structureLocked && (
                        <button
                          className="delete-module-btn"
                          onClick={() => handleDeleteModule(role.name, module)}
                          disabled={isProcessing}
                          title={`Delete ${module}`}
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="role-no-modules">No modules defined</div>
              )}
            </div>
          ))
        ) : (
          <div className="role-no-data">No role data available</div>
        )}
      </div>

      {/* Add Module Section */}
      {!structureLocked && (
        <div className="add-module-section">
          <form onSubmit={handleAddModule} className="add-module-form">
            <div className="role-selection">
              <label className="role-selection-label">Select role to show a specific role's module</label>
              <div className="role-radio-group">
                {roles.map((role, idx) => (
                  <label key={idx} className="role-radio-label">
                    <input
                      type="radio"
                      name="selectedRole"
                      value={role.name}
                      checked={selectedRole === role.name}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="role-radio-input"
                    />
                    <span className="role-radio-text">{role.name}</span>
                  </label>
                ))}
              </div>
              {selectedRole ? (
                <button
                  type="button"
                  className="add-module-show-all-link"
                  onClick={() => setSelectedRole('')}
                >
                  Show all role's modules
                </button>
              ) : null}
            </div>
            <h4 className="add-module-title">Add New Module</h4>
            <div className="module-input-group">
              <input
                type="text"
                value={newModuleName}
                onChange={(e) => setNewModuleName(e.target.value)}
                placeholder="Enter module name..."
                className="module-input"
              />
              <button
                type="submit"
                disabled={!newModuleName.trim() || !selectedRole || isProcessing}
                className="add-module-btn"
              >
                {isProcessing ? 'Processing...' : 'Add Module'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="role-modules-hint">
        <span className="hint-text">You can ask questions about this estimation anytime!</span>
      </div>

      {/* Toast Notification - Rendered via Portal */}
      {toastMessage && createPortal(
        <div className="module-toast">
          <div className="module-toast-content">
            <span className="module-toast-icon">✓</span>
            <span className="module-toast-message">{toastMessage}</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Role-Platform Assignment content component
function RolePlatformAssignmentContent({ message, onOptionClick }) {
  const content = message.content;
  const slot = message.slot;
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const { toastMessage, showToast } = useToast();

  // Parse the content to extract roles, platform options, and current assignments
  const parseContent = (text) => {
    const lines = text.split('\n').map(l => l.trim());

    const roles = [];
    // Backend-aligned labels (PLATFORM_DISPLAY_ORDER: Mobile Application, Web Application/Website)
    const platforms = ['Mobile Application', 'Web Application/Website'];
    const currentAssignments = {};
    let inRolesSection = false;
    let inPlatformSection = false;
    let inAssignmentsSection = false;

    for (const line of lines) {
      if (!line) continue;

      // Detect sections
      if (line.includes('**Roles**') || line === '**Roles**') {
        inRolesSection = true;
        inPlatformSection = false;
        inAssignmentsSection = false;
        continue;
      }

      if (line.includes('**Select Platform') || line.includes('Select Platform') ||
        line.includes('Select Platform for your roles')) {
        inRolesSection = false;
        inPlatformSection = true;
        inAssignmentsSection = false;
        continue;
      }

      if (line.includes('**Current Assignments:**') || line.includes('Current Assignments')) {
        inRolesSection = false;
        inPlatformSection = false;
        inAssignmentsSection = true;
        continue;
      }

      // Skip instruction lines
      if (line.includes('**Instructions:**') || line.includes('Instructions') ||
        (line.startsWith('•') && (line.includes('Select roles') || line.includes('Select a platform') ||
          line.includes('Selected roles') || line.includes('Unselected roles')))) {
        continue;
      }

      // Skip Continue button text (but not if it's in options)
      if ((line.includes('(Continue)') || line === 'Continue') && !message.options?.includes('Continue')) {
        continue;
      }

      // Parse roles (checkboxes) - handle both ☐ and ☑
      if (inRolesSection) {
        // Match ☐ or ☑ followed by role name
        const roleMatch = line.match(/[☐☑]\s*(.+)/);
        if (roleMatch) {
          const roleName = roleMatch[1].trim();
          const isChecked = line.startsWith('☑');
          // Avoid duplicates
          if (!roles.find(r => r.name === roleName)) {
            roles.push({ name: roleName, checked: isChecked });
          }
        }
      }

      // Parse platforms (radio buttons) - use backend-aligned full labels
      if (inPlatformSection) {
        const trimmedLine = line.trim();
        if (trimmedLine.toLowerCase().includes('select platform') ||
          !trimmedLine ||
          trimmedLine.includes('(Continue)') ||
          trimmedLine === 'Continue') {
          continue;
        }
        // Normalize to backend-aligned labels (accept "Mobile"/"Web" from content for robustness)
        let normalized = trimmedLine;
        if (trimmedLine === 'Mobile' || trimmedLine.toLowerCase().includes('mobile')) {
          normalized = 'Mobile Application';
        } else if (trimmedLine === 'Web' || trimmedLine.toLowerCase().includes('web') || trimmedLine.toLowerCase().includes('website')) {
          normalized = 'Web Application/Website';
        }
        if ((normalized === 'Mobile Application' || normalized === 'Web Application/Website') && !platforms.includes(normalized)) {
          platforms.push(normalized);
        }
      }

      // Parse current assignments - store full backend-aligned labels
      if (inAssignmentsSection) {
        const assignmentMatch = line.match(/•\s*(.+?):\s*(.+)/);
        if (assignmentMatch) {
          const roleName = assignmentMatch[1].trim();
          let platformName = assignmentMatch[2].trim();
          if (platformName.toLowerCase().includes('mobile')) {
            platformName = 'Mobile Application';
          } else if (platformName.toLowerCase().includes('web') || platformName.toLowerCase().includes('website')) {
            platformName = 'Web Application/Website';
          }
          currentAssignments[roleName] = platformName;
        }
      }
    }

    // If no roles found in parsing, try to extract from any line with checkbox
    if (roles.length === 0) {
      for (const line of lines) {
        const roleMatch = line.match(/[☐☑]\s*(.+)/);
        if (roleMatch) {
          const roleName = roleMatch[1].trim();
          if (!roles.find(r => r.name === roleName)) {
            roles.push({ name: roleName, checked: line.startsWith('☑') });
          }
        }
      }
    }

    // Ensure we have default platforms if none were parsed
    if (platforms.length === 0) {
      platforms.push('Mobile Application', 'Web Application/Website');
    }

    return { roles, platforms, currentAssignments };
  };

  // Memoized on `content` only, so `roles`/`currentAssignments` keep a stable
  // identity across re-renders triggered by the user's own selections (role
  // toggle, platform pick) — otherwise the effect below re-fires on every
  // click and immediately resets selectedRoles back to currentAssignments,
  // making role selection appear broken.
  const { roles, platforms, currentAssignments } = useMemo(
    () => parseContent(content),
    [content]
  );

  // Initialize selected roles from parsed content and current assignments
  useEffect(() => {
    // First, check if roles have checked state from parsing
    const checkedRoles = roles.filter(r => r.checked).map(r => r.name);
    if (checkedRoles.length > 0) {
      setSelectedRoles(checkedRoles);
    }

    // Then, check current assignments
    if (Object.keys(currentAssignments).length > 0) {
      const assignedRoles = Object.keys(currentAssignments);
      setSelectedRoles(assignedRoles);

      const uniquePlatforms = [...new Set(Object.values(currentAssignments))];
      if (uniquePlatforms.length === 1) {
        const platform = uniquePlatforms[0];
        const normalized = platform.includes('Mobile') ? 'Mobile Application' :
          (platform.includes('Web') || platform.includes('Website')) ? 'Web Application/Website' : platform;
        setSelectedPlatform(normalized);
      }
    }

    // If platforms are available but none selected, and we have selected roles, 
    // we might want to auto-select first platform (optional - remove if not needed)
  }, [content, roles, currentAssignments]); // Re-run when content changes

  const handleRoleToggle = (roleName) => {
    setSelectedRoles(prev => {
      if (prev.includes(roleName)) {
        return prev.filter(r => r !== roleName);
      } else {
        return [...prev, roleName];
      }
    });
  };

  const handlePlatformSelect = (platformName) => {
    setSelectedPlatform(platformName);
  };

  const handleContinue = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      // If user has made selections, send them as JSON silently (don't show in chat)
      if (selectedRoles.length > 0 && selectedPlatform) {
        // Backend expects full labels: "Mobile Application" or "Web Application/Website"
        const selectionData = {
          roles: selectedRoles,
          platform: selectedPlatform
        };

        // Send the selection data to backend silently (won't show JSON in chat)
        await onOptionClick(JSON.stringify(selectionData), {
          explicitSlot: slot,
          silent: true,
          updateExisting: true
        });
      } else {
        // If no selections made, just send "Continue" silently
        await onOptionClick('Continue', {
          explicitSlot: slot,
          silent: true,
          updateExisting: true
        });
      }
    } catch (error) {
      showToast('Failed to submit selections');
      console.error('Error submitting role-platform assignment:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="role-platform-assignment">
      <div className="role-platform-header">
        <h3 className="role-platform-title">Assign Roles to Platform</h3>
        <p className="role-platform-description">
          Select roles and assign them to Mobile Application or Web Application/Website. Unselected roles will be automatically assigned to the other platform.
        </p>
      </div>

      {/* Roles Section */}
      <div className="role-platform-section">
        <h4 className="section-title">Roles</h4>
        <div className="roles-checkbox-list">
          {roles.map((role, index) => {
            const isSelected = selectedRoles.includes(role.name);
            return (
              <button
                key={index}
                type="button"
                onClick={() => handleRoleToggle(role.name)}
                className={`role-checkbox-button ${isSelected ? 'selected' : ''}`}
              >
                <div className="round-checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => { }}
                    className="round-checkbox"
                    readOnly
                  />
                </div>
                <span className="checkbox-label">{role.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Platform Section */}
      <div className="role-platform-section">
        <h4 className="section-title">Select Platform for your roles</h4>
        <div className="platform-radio-list">
          {platforms.map((platform, index) => {
            const isSelected = selectedPlatform === platform;
            return (
              <button
                key={index}
                type="button"
                onClick={() => handlePlatformSelect(platform)}
                className={`platform-radio-button ${isSelected ? 'selected' : ''}`}
              >
                <div className="round-radio-wrapper">
                  <input
                    type="radio"
                    name="platform"
                    value={platform}
                    checked={isSelected}
                    onChange={() => { }}
                    className="round-radio"
                    readOnly
                  />
                </div>
                <span className="radio-label">{platform}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Current Assignments Display (if any) */}
      {Object.keys(currentAssignments).length > 0 && (
        <div className="role-platform-section">
          <h4 className="section-title">Current Assignments</h4>
          <div className="assignments-list">
            {Object.entries(currentAssignments).map(([role, platform], index) => (
              <div key={index} className="assignment-item">
                <span className="assignment-role">{role}:</span>
                <span className="assignment-platform">{platform}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Continue Button */}
      <div className="role-platform-continue-section">
        <button
          onClick={handleContinue}
          disabled={isProcessing || selectedRoles.length === 0 || !selectedPlatform}
          className="role-platform-continue-btn"
        >
          {isProcessing ? 'Processing...' : 'Continue'}
        </button>
      </div>

      {/* Toast Notification */}
      {toastMessage && createPortal(
        <div className="module-toast">
          <div className="module-toast-content">
            <span className="module-toast-icon">✓</span>
            <span className="module-toast-message">{toastMessage}</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Role-Platform Modules content component (after assignment, grouped by platform)
function RolePlatformModulesContent({ message, onOptionClick, structureLocked = false }) {
  const content = message.content;

  // Parse the content to extract roles with platforms and modules grouped by platform
  // Expected backend format:
  // Roles:
  // {role name} - {platform name}
  // {role name} - {platform name}
  // ...
  // 📦 Key Modules for {platform name}
  // {role name}
  // • {module name}
  // • {module name}
  // ...
  // Backend format: "👥 **Role Platform Assignments**" then "• Platform → Role1, Role2"
  // then "📦 **Key Modules for {platform}**", "### 👤 **Role**", "• Module"
  const parseContent = (text) => {
    const lines = text.split('\n').map(l => l.trim());
    const roleAssignments = [];
    const modulesByPlatform = {};

    const cleanRoleName = (raw) => {
      if (!raw) return raw;
      return raw.replace(/^###\s*👤\s*\*?\*?|\*?\*?$/g, '').trim() || raw;
    };

    let inAssignmentsSection = false;
    let inModulesSection = false;
    let currentPlatform = null;
    let currentRole = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line) continue;

      // "👥 **Role Platform Assignments**" or "Roles:"
      if (line.includes('Role Platform Assignments') || line === 'Roles:' || line.startsWith('Roles:')) {
        inAssignmentsSection = true;
        inModulesSection = false;
        currentPlatform = null;
        currentRole = null;
        continue;
      }

      const modulesHeaderMatch = line.match(/📦\s*\*?\*?Key\s*Modules\s*for\s*(.+)/i);
      if (modulesHeaderMatch) {
        inAssignmentsSection = false;
        inModulesSection = true;
        currentPlatform = modulesHeaderMatch[1].trim().replace(/\*+$/g, '').trim();
        currentRole = null;
        if (!modulesByPlatform[currentPlatform]) {
          modulesByPlatform[currentPlatform] = {};
        }
        continue;
      }

      // Assignments: "• Platform → Role1, Role2" (backend format)
      if (inAssignmentsSection) {
        const platformToRolesMatch = line.match(/^[•\*]\s*(.+?)\s*→\s*(.+)$/);
        if (platformToRolesMatch) {
          const platformName = platformToRolesMatch[1].trim();
          const rolesStr = platformToRolesMatch[2].trim();
          rolesStr.split(',').forEach((r) => {
            const roleName = r.trim();
            if (roleName) roleAssignments.push({ role: roleName, platform: platformName });
          });
          continue;
        }
        // Legacy: "Role Name - Platform Name"
        const assignmentMatch = line.match(/^(.+?)\s*-\s*(.+)$/);
        if (assignmentMatch) {
          const roleName = assignmentMatch[1].trim();
          const platformName = assignmentMatch[2].trim();
          roleAssignments.push({ role: roleName, platform: platformName });
        }
      }

      if (inModulesSection && currentPlatform) {
        if (line && !line.startsWith('•') && !line.startsWith('📦') && !line.includes('Key Modules') && !line.includes('Modules for')) {
          let nextNonEmptyLine = null;
          for (let j = i + 1; j < lines.length; j++) {
            if (lines[j]) {
              nextNonEmptyLine = lines[j];
              break;
            }
          }
          if (nextNonEmptyLine && nextNonEmptyLine.startsWith('•')) {
            currentRole = cleanRoleName(line);
            if (!modulesByPlatform[currentPlatform][currentRole]) {
              modulesByPlatform[currentPlatform][currentRole] = [];
            }
            continue;
          }
        }
        if (line.startsWith('•') && currentRole) {
          const moduleName = line.replace(/^•\s*/, '').trim();
          if (moduleName && !moduleName.startsWith('_') && moduleName.toLowerCase() !== 'no modules defined') {
            modulesByPlatform[currentPlatform][currentRole].push(moduleName);
          }
        }
      }
    }

    return { roleAssignments, modulesByPlatform };
  };

  const parsed = parseContent(content);
  let { roleAssignments, modulesByPlatform } = parsed;

  const platforms = Object.keys(modulesByPlatform);
  if (roleAssignments.length === 0 && platforms.length > 0) {
    roleAssignments = platforms.flatMap((platform) =>
      (Object.keys(modulesByPlatform[platform] || {})).map((role) => ({ role, platform }))
    );
  }

  const rolesByPlatform = {};
  roleAssignments.forEach((assignment) => {
    const platform = assignment.platform;
    if (!rolesByPlatform[platform]) rolesByPlatform[platform] = [];
    rolesByPlatform[platform].push(assignment.role);
  });
  if (Object.keys(rolesByPlatform).length === 0 && platforms.length > 0) {
    platforms.forEach((platform) => {
      rolesByPlatform[platform] = Object.keys(modulesByPlatform[platform] || {});
    });
  }

  const allRoles = [...new Set(roleAssignments.map((a) => a.role))].sort();

  const [newModuleName, setNewModuleName] = useState('');
  const [selectedRoleForModule, setSelectedRoleForModule] = useState('');
  const [selectedModule, setSelectedModule] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toastMessage, showToast } = useToast();

  // Helper to find platform for a role
  const getPlatformForRole = (roleName) => {
    const assignment = roleAssignments.find(a => a.role === roleName);
    return assignment ? assignment.platform : null;
  };

  const handleDeleteModule = async (platform, role, moduleName) => {
    if (structureLocked || isProcessing) return;
    setIsProcessing(true);
    try {
      // Backend expects: Delete module "Module Name" from Role role
      const deleteCommand = `Delete module "${moduleName}" from ${role} role`;
      await onOptionClick(deleteCommand, {
        explicitSlot: 'modules.edit',
        silent: true,
        updateExisting: true
      });
      showToast(`Module "${moduleName}" deleted successfully`);
    } catch (error) {
      showToast(`Failed to delete module "${moduleName}"`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddModule = async (e) => {
    e.preventDefault();
    const moduleName = newModuleName.trim();
    if (structureLocked || !moduleName || !selectedRoleForModule || isProcessing) {
      return;
    }
    setIsProcessing(true);
    try {
      const addCommand = `Add module "${moduleName}" to ${selectedRoleForModule} role`;
      const response = await onOptionClick(addCommand, {
        explicitSlot: 'modules.edit',
        silent: true,
        updateExisting: true
      });
      const isDuplicate = response?.aiReply && (
        response.aiReply.includes('already exists') ||
        response.aiReply.includes('module already')
      );
      if (isDuplicate) {
        showToast('Module is already present.');
      } else {
        showToast(`Module "${moduleName}" added successfully`);
      }
      setNewModuleName('');
      setSelectedRoleForModule('');
    } catch (error) {
      showToast(`Failed to add module "${moduleName}"`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Get project name from role assignments (first role's platform or a default)
  const projectName = platforms.length > 0 ? platforms[0] : 'Project';

  const filterRoleEntries = (entries) => {
    if (!selectedRoleForModule) return entries;
    return entries.filter(([role]) => role === selectedRoleForModule);
  };

  return (
    <div className="role-modules-display role-modules-display-multi-platform">
      {/* Header Section - Multi-Platform: Role Platform Assignments + Key Modules per Platform */}
      <div className="role-modules-header">
        <div>
          <h2>👥 Role Platform Assignments & Key Modules</h2>
          <span className="role-modules-badge">Multi-Platform</span>
        </div>
        {Object.keys(rolesByPlatform).length > 0 && (
          <div className="role-modules-project-info">
            {Object.entries(rolesByPlatform).map(([platform, roles]) => (
              <div key={platform}>
                <span className="platform-name"><strong>{platform}:</strong> {roles.join(', ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Body Section */}
      <div className="role-modules-body">
        {platforms.length > 0 ? (
          platforms.map((platform, platformIndex) => {
            const roleEntries = filterRoleEntries(
              Object.entries(modulesByPlatform[platform] || {})
            );
            if (roleEntries.length === 0) return null;
            return (
              <div key={platformIndex} className="platform-modules-group">
                <h3 className="platform-modules-header">{platform}:</h3>
                <div className="platform-modules-content">
                  {roleEntries.map(([role, modules], roleIndex) => {
                    const hasSelected = modules.some((module) => {
                      const moduleKey = `${platform}-${role}-${module}`;
                      return selectedModule === moduleKey;
                    });
                    return (
                      <div key={roleIndex} className={`role-card ${hasSelected ? 'has-selected' : ''}`}>
                        <div className="role-header">
                          <span className="role-icon">👤</span>
                          <h3 className="role-name">{role}</h3>
                        </div>
                        {modules.length > 0 ? (
                          <ul className="role-modules-list">
                            {modules.map((module, moduleIndex) => {
                              const moduleKey = `${platform}-${role}-${module}`;
                              const isSelected = selectedModule === moduleKey;
                              return (
                                <li
                                  key={moduleIndex}
                                  className={`role-module-item ${isSelected ? 'module-item-selected' : ''}`}
                                  onClick={() => setSelectedModule(isSelected ? null : moduleKey)}
                                >
                                  <span className="module-text">{module}</span>
                                  {!structureLocked && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteModule(platform, role, module);
                                      }}
                                      disabled={isProcessing}
                                      className="module-delete-btn"
                                      aria-label={`Delete ${module}`}
                                    >
                                      <DeleteIcon />
                                    </button>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="role-no-modules">No modules defined</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        ) : (
          <div className="role-no-data">No modules available</div>
        )}
      </div>

      {/* Single Add Module Section (not platform-wise) */}
      {platforms.length > 0 && allRoles.length > 0 && !structureLocked && (
        <div className="add-module-section">
          <form onSubmit={handleAddModule} className="add-module-form">
            <div className="add-module-role-selection">
              <label className="add-module-role-label">Select role to show a specific role's module</label>
              <div className="module-role-radio-group">
                {allRoles.map((role) => (
                  <label key={role} className="module-role-radio-label">
                    <input
                      type="radio"
                      name="role-selection"
                      value={role}
                      checked={selectedRoleForModule === role}
                      onChange={(e) => setSelectedRoleForModule(e.target.value)}
                      className="module-role-radio"
                      required
                    />
                    <span className="module-role-radio-text">{role}</span>
                  </label>
                ))}
              </div>
              {selectedRoleForModule ? (
                <button
                  type="button"
                  className="add-module-show-all-link"
                  onClick={() => setSelectedRoleForModule('')}
                >
                  Show all role's modules
                </button>
              ) : null}
            </div>
            <h4 className="add-module-title">Add New Module</h4>
            <div className="add-module-inputs">
              <input
                type="text"
                value={newModuleName}
                onChange={(e) => setNewModuleName(e.target.value)}
                placeholder="Enter module name..."
                className="module-name-input"
                required
              />
              <button
                type="submit"
                disabled={!newModuleName.trim() || !selectedRoleForModule || isProcessing}
                className={`add-module-submit-btn ${newModuleName.trim() && selectedRoleForModule ? 'add-module-submit-btn-active' : ''}`}
              >
                {isProcessing ? 'Processing...' : 'Add Module'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Fallback: If parsing didn't work, show raw content */}
      {roleAssignments.length === 0 && platforms.length === 0 && (
        <div className="message-content">
          <Markdown remarkPlugins={[remarkGfm]}>
            {content}
          </Markdown>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && createPortal(
        <div className="module-toast">
          <div className="module-toast-content">
            <span className="module-toast-icon">✓</span>
            <span className="module-toast-message">{toastMessage}</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Typing indicator
function TypingIndicator() {
  return (
    <div className="message-row">
      <div className="avatar avatar-assistant">
        <BotIcon />
      </div>
      <div className="message-bubble bubble-assistant glass-bubble">
        <div className="loader">
          <div className="spinner"></div>
          <span>Thinking...</span>
        </div>
      </div>
    </div>
  );
}

const STEP_INITIALIZER_STAGES = [
  { id: 'project_idea', label: 'Project idea', helper: 'Describe your app or platform idea' },
  { id: 'platform_assignment', label: 'Platform assignment', helper: 'Choose the target platform and app type' },
  { id: 'roles', label: 'Roles', helper: 'Review or refine the project roles' },
  { id: 'modules', label: 'Modules', helper: 'Validate the generated modules' },
  { id: 'features', label: 'Features', helper: 'Check the scoped features and layers' },
  { id: 'estimation', label: 'Estimation', helper: 'Review the final effort breakdown' },
];

const getMessageContent = (message) => String(message?.content || '');

const isAssistantMessage = (message) => message?.role === 'assistant';
const isUserMessage = (message) => message?.role === 'user';
const isSystemStartMessage = (message) => getMessageContent(message).includes('Hello! I’m your Estimation Assistant');

const isRolesStageMessage = (message) => {
  const content = getMessageContent(message);
  return isAssistantMessage(message) && (
    content.includes('👥 **Project Roles**') ||
    (message.slot === 'roles.items' && content.includes('•'))
  );
};

const isPlatformAssignmentStageMessage = (message) => {
  const content = getMessageContent(message);
  const lowerContent = content.toLowerCase();
  const options = Array.isArray(message?.options) ? message.options : [];
  const hasPlatformOptions = options.some((option) => {
    const normalized = String(option).toLowerCase();
    return (
      normalized.includes('mobile') ||
      normalized.includes('web') ||
      normalized.includes('desktop') ||
      normalized.includes('power app')
    );
  });

  return isAssistantMessage(message) && (
    message.slot === 'role_platform_assignments' ||
    (content.includes('**Roles**') && content.includes('**Select Platform for your roles**')) ||
    content.includes('Role Platform Assignments') ||
    lowerContent.includes('which primary platform') ||
    lowerContent.includes('which platform') ||
    lowerContent.includes('which type of mobile application') ||
    lowerContent.includes('which type of web application') ||
    lowerContent.includes('which type of desktop application') ||
    hasPlatformOptions
  );
};

const isModulesStageMessage = (message) => {
  const content = getMessageContent(message);
  const isMultiPlatformModules = content.includes('Key Modules for') || content.includes('Role Platform Assignments');
  // features.review reuses the "### 👤 **" module markup for its module/feature
  // headers — it must never be counted as the Modules stage.
  const isSinglePlatformModules = !isMultiPlatformModules && message.slot !== 'features.review' && (
    content.includes('🚀 **Project Modules by Role**') ||
    content.includes('### 👤 **') ||
    message.slot === 'modules.edit'
  );

  return isAssistantMessage(message) && (isMultiPlatformModules || isSinglePlatformModules);
};

const isFeaturesStageMessage = (message) => {
  const content = getMessageContent(message);
  return isAssistantMessage(message) && (
    message.slot === 'features.review' ||
    (content.includes('Modules:') && content.includes('Project Scope:')) ||
    (content.includes('Project Scope:') && (
      content.includes('Frontend:') ||
      content.includes('Backend/API:') ||
      content.includes('Core API and Admin Backend:') ||
      content.includes('Admin Panel Frontend:') ||
      content.includes('UI/UX:')
    ))
  );
};

const isEstimationStageMessage = (message) => {
  const content = getMessageContent(message);
  // features.review carries project_scope too (for the editor), but hours don't
  // exist yet — it must not be counted as having reached the Estimation stage.
  if (message.slot === 'features.review') return false;
  return isAssistantMessage(message) && (
    content.toLowerCase().includes('total estimated hours') ||
    content.toLowerCase().includes('total hours') ||
    content.includes('🚀 **Project Estimation & Scope of Work**') ||
    content.includes('### 📦 Key Modules') ||
    content.includes('### 🛠️ Detailed App Layers per Module') ||
    (content.includes('Project Estimation & Scope of Work') && (
      content.includes('Detailed Scope') ||
      content.includes('Frontend:') ||
      content.includes('Backend/API:')
    )) ||
    (message.project_scope && typeof message.project_scope === 'object' && Object.keys(message.project_scope).length > 0) ||
    message.estimation_value
  );
};

/** Editable Detailed Scope (add/delete) — what “Features” should scroll to. Mirrors EstimationContent.hasEditableScope. */
const isEditableFeaturesScopeMessage = (message) =>
  isAssistantMessage(message) &&
  message.slot !== 'estimation.final' &&
  message.project_scope &&
  typeof message.project_scope === 'object' &&
  Object.keys(message.project_scope).length > 0;

/** Fallback: read-only features list (Modules + Project Scope text), not modules-only. */
const isReadOnlyFeaturesListMessage = (message) => {
  if (!isAssistantMessage(message) || message.estimation_value) return false;
  if (isModulesStageMessage(message) && !isFeaturesStageMessage(message)) return false;
  return isFeaturesStageMessage(message);
};

/**
 * Full estimation with App layers + hours — no editable project_scope on same message
 * (backend usually sends hours on a follow-up message after Confirm).
 */
const isEstimationAppLayersMessage = (message) => {
  if (!isAssistantMessage(message) || !message.estimation_value) return false;
  const ps = message.project_scope;
  if (ps && typeof ps === 'object' && Object.keys(ps).length > 0) return false;
  return true;
};

/** Content has app layer summary (QA + Frontend hrs) when API shape differs. */
const contentHasAppLayerSummary = (message) => {
  const c = getMessageContent(message);
  if (!c.includes('Scope of Work') && !c.includes('Project Estimation')) return false;
  const qa = /QA\s*:\s*\d+\s*hrs/i.test(c);
  const fe =
    /Frontend\s*\([^)]+\)\s*:\s*\d+\s*hrs/i.test(c) ||
    /Frontend\s*:\s*\d+\s*hrs/i.test(c) ||
    /Backend\/API\s*:\s*\d+\s*hrs/i.test(c);
  return qa && fe;
};

/**
 * Scroll target for “Estimation”: final card with App layers + Key Modules + hours —
 * not the editable Features / Detailed Scope bubble and not the modules-only picker.
 */
const isEstimationAnchorMessage = (message) => {
  if (!isAssistantMessage(message)) return false;
  if (isEditableFeaturesScopeMessage(message)) return false;

  if (message.slot === 'estimation.final') return true;
  if (contentHasAppLayerSummary(message)) return true;
  if (isEstimationAppLayersMessage(message)) return true;

  const c = getMessageContent(message);
  const lower = c.toLowerCase();

  if (isModulesStageMessage(message) && !lower.includes('scope of work') && !lower.includes('project estimation')) {
    return false;
  }

  const hasEstimationHeader =
    c.includes('Project Estimation & Scope of Work') ||
    c.includes('🚀 **Project Estimation') ||
    (lower.includes('project estimation') && lower.includes('scope of work'));

  const hasAppLayersOrModulesBreakdown =
    lower.includes('app layers') ||
    c.includes('### 📦 Key Modules') ||
    c.includes('### 🛠️ Detailed App Layers per Module') ||
    lower.includes('detailed app layers per module');

  if (hasEstimationHeader && hasAppLayersOrModulesBreakdown) return true;
  if (hasEstimationHeader && (lower.includes('total estimated') || lower.includes('total hours'))) return true;
  if (message.estimation_value && hasAppLayersOrModulesBreakdown) return true;

  return false;
};

function hasSubmittedProjectIdea(messages = []) {
  return messages.some((message) => isUserMessage(message) && !isSystemStartMessage(message));
}

function getStepInitializerState(messages = []) {
  const assistantMessages = messages.filter(isAssistantMessage);
  const userMessages = messages.filter((message) => isUserMessage(message) && !isSystemStartMessage(message));
  const hasProjectIdea = userMessages.length > 0;
  const hasPlatformAssignment = assistantMessages.some(isPlatformAssignmentStageMessage);
  const hasRoles = assistantMessages.some(isRolesStageMessage);
  const hasModules = assistantMessages.some(isModulesStageMessage);
  const hasFeatures = assistantMessages.some(isFeaturesStageMessage) || assistantMessages.some(isEstimationStageMessage);
  const hasEstimation = assistantMessages.some(isEstimationStageMessage);
  const hasFinalEstimationWithHours = assistantMessages.some(isEstimationAnchorMessage);
  const hasProgressedPastPlatform = hasRoles || hasModules || hasFeatures || hasEstimation;

  const visibleStages = STEP_INITIALIZER_STAGES.filter((stage) => {
    if (stage.id === 'platform_assignment') {
      return hasPlatformAssignment || hasProgressedPastPlatform;
    }
    return true;
  });

  let currentStageId = 'project_idea';

  if (hasEstimation) {
    currentStageId = 'estimation';
  } else if (hasFeatures) {
    currentStageId = 'features';
  } else if (hasModules) {
    currentStageId = 'modules';
  } else if (hasRoles) {
    currentStageId = 'roles';
  } else if (hasPlatformAssignment || hasProjectIdea) {
    currentStageId = 'platform_assignment';
  }

  const currentIndex = Math.max(visibleStages.findIndex((stage) => stage.id === currentStageId), 0);

  const stages = visibleStages.map((stage, index) => {
    let status = index === currentIndex ? 'current' : 'pending';

    if (hasFinalEstimationWithHours) {
      status = 'completed';
    } else if (stage.id === 'project_idea' && hasProjectIdea) {
      status = currentStageId === 'project_idea' ? 'current' : 'completed';
    } else if (index < currentIndex) {
      status = 'completed';
    }

    return {
      ...stage,
      number: index + 1,
      status,
    };
  });

  return {
    stages,
    currentStageLabel: stages[currentIndex]?.label || 'Project idea',
    completedCount: stages.filter((stage) => stage.status === 'completed').length,
    pendingCount: stages.filter((stage) => stage.status === 'pending').length,
  };
}

/**
 * Features → editable Detailed Scope (project_scope) before the App-layers estimation message;
 * fallback to read-only features list. Estimation → last message matching final estimation (most recent report).
 */
function getStepMessageAnchors(messages = []) {
  const anchors = {};
  if (messages.length > 0) {
    anchors.project_idea = 0;
  }

  let firstEstimationIndex = -1;
  for (let i = 0; i < messages.length; i++) {
    if (isEstimationAnchorMessage(messages[i])) {
      firstEstimationIndex = i;
      break;
    }
  }

  let lastFeaturesIdx = -1;
  const beforeEstLimit = firstEstimationIndex >= 0 ? firstEstimationIndex : messages.length;
  for (let i = 0; i < beforeEstLimit; i++) {
    if (isEditableFeaturesScopeMessage(messages[i])) lastFeaturesIdx = i;
  }
  if (lastFeaturesIdx < 0) {
    for (let i = 0; i < beforeEstLimit; i++) {
      if (isReadOnlyFeaturesListMessage(messages[i])) lastFeaturesIdx = i;
    }
  }
  if (lastFeaturesIdx < 0) {
    for (let i = 0; i < messages.length; i++) {
      if (isEditableFeaturesScopeMessage(messages[i])) lastFeaturesIdx = i;
    }
  }
  if (lastFeaturesIdx < 0) {
    for (let i = 0; i < messages.length; i++) {
      if (isReadOnlyFeaturesListMessage(messages[i])) lastFeaturesIdx = i;
    }
  }
  if (lastFeaturesIdx >= 0) anchors.features = lastFeaturesIdx;

  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (anchors.platform_assignment === undefined && isPlatformAssignmentStageMessage(m)) {
      anchors.platform_assignment = i;
    }
    if (anchors.roles === undefined && isRolesStageMessage(m)) {
      anchors.roles = i;
    }
    if (anchors.modules === undefined && isModulesStageMessage(m)) {
      anchors.modules = i;
    }
    if (isEstimationAnchorMessage(m)) {
      anchors.estimation = i;
    }
  }
  return anchors;
}

function messageIndicesToStepStages(anchors) {
  const byIndex = {};
  for (const [stageId, idx] of Object.entries(anchors)) {
    if (!byIndex[idx]) byIndex[idx] = [];
    byIndex[idx].push(stageId);
  }
  return byIndex;
}

function StepInitializer({ progress, compact = false, variant, onStepClick, stepAnchors = {} }) {
  const isSidebar = variant === 'sidebar';
  const total = progress.stages?.length ?? 0;
  const completedCount = progress.completedCount ?? 0;
  const progressPercent = total > 0 ? (completedCount / total) * 100 : 0;
  return (
    <section
      className={`step-initializer glass-panel ${compact ? 'step-initializer-compact' : ''} ${isSidebar ? 'step-initializer-sidebar' : ''}`}
      aria-label="Conversation step progress"
    >
      <div className="step-initializer-header">
        <div className="step-initializer-copy">
          <span className="step-initializer-kicker">MISSION MAP</span>
          {isSidebar ? (
            <p className="step-initializer-subtitle step-initializer-sidebar-desc">Mission map for the current product scope</p>
          ) : (
            <>
              <h2 className="step-initializer-title">Follow the scope path</h2>
              <p className="step-initializer-subtitle">
                Current step: <strong>{progress.currentStageLabel}</strong>
              </p>
            </>
          )}
        </div>

        {isSidebar ? (
          <div className="step-initializer-summary step-initializer-summary-sidebar" aria-label="Step progress">
            <div className="step-summary-status-line">
              <span className="step-summary-status-dot" aria-hidden="true" />
              <span className="step-summary-status-copy">
                {progress.currentStageLabel || 'Awaiting first input'}
              </span>
            </div>
            <div className="step-summary-progress-track">
              <div className="step-summary-progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>
            <span className="step-summary-com-text">{completedCount} complete of {total}</span>
          </div>
        ) : (
          <div className="step-initializer-summary" aria-label="Step counts">
            <div className="step-summary-pill">
              <strong>{progress.completedCount}</strong>
              <span>Completed</span>
            </div>
            <div className="step-summary-pill">
              <strong>{progress.pendingCount}</strong>
              <span>Pending</span>
            </div>
          </div>
        )}
      </div>

      {/* All steps are clickable; scroll goes to that step's first message in chat when present. Whole row (icon + label) is clickable. */}
      <div className="step-initializer-list">
        {progress.stages.map((stage) => {
          const isClickable = typeof onStepClick === 'function';
          const itemClass = `step-initializer-item step-${stage.status}${isClickable ? ' step-initializer-item-clickable' : ''}`;
          const inner = (
            <>
              <div className="step-initializer-marker" aria-hidden="true">
                {stage.status === 'completed' ? <CheckIcon /> : <span>{stage.number}</span>}
              </div>
              <div className="step-initializer-rail" aria-hidden="true" />
              <div className="step-initializer-text">
                <span className="step-initializer-label">{stage.label}</span>
                <span className="step-initializer-helper">{stage.helper}</span>
              </div>
            </>
          );
          if (isClickable) {
            return (
              <button
                key={stage.id}
                type="button"
                className={itemClass}
                aria-label={`Go to ${stage.label} in chat`}
                onClick={() => onStepClick(stage.id)}
              >
                {inner}
              </button>
            );
          }
          return (
            <div
              key={stage.id}
              className={itemClass}
              aria-current={stage.status === 'current' ? 'step' : undefined}
            >
              {inner}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// System message to send on page load/reload/fresh start
const SYSTEM_START_MESSAGE =
  "👋 Hello! I’m your Estimation Assistant.\nLet’s build something great together — start by describing your project idea.";

const UPLOAD_STAGE_LABELS = [
  'Preparing your document…',
  'Parsing document structure…',
  'Extracting roles & modules…',
  'Mapping features & layers…',
  'Building consolidated feature list…',
  'Refining scope for review…',
];

const UPLOAD_PROGRESS_CAP = 97;
const UPLOAD_PROGRESS_TICK_MS = 450;
const UPLOAD_PROGRESS_SOFT_CAP_MS = 90000;
const UPLOAD_COMPLETE_PAUSE_MS = 320;

function getUploadStageIndex(progress) {
  if (progress >= 82) return 5;
  if (progress >= 64) return 4;
  if (progress >= 48) return 3;
  if (progress >= 32) return 2;
  if (progress >= 16) return 1;
  return 0;
}

function computeUploadProgressTick(prevProgress, elapsedMs) {
  let increment;
  if (elapsedMs < 6000) increment = 0.14;
  else if (elapsedMs < 18000) increment = 0.24;
  else if (elapsedMs < 36000) increment = 0.34;
  else if (elapsedMs < 72000) increment = 0.28;
  else if (elapsedMs < 120000) increment = 0.18;
  else increment = 0.1;

  const softCap = Math.min(
    UPLOAD_PROGRESS_CAP,
    UPLOAD_PROGRESS_CAP * (1 - Math.exp(-elapsedMs / UPLOAD_PROGRESS_SOFT_CAP_MS))
  );

  let next = Math.min(softCap, prevProgress + increment);
  if (next <= prevProgress + 0.04 && prevProgress < UPLOAD_PROGRESS_CAP - 0.05) {
    next = Math.min(UPLOAD_PROGRESS_CAP, prevProgress + 0.1);
  }
  return Math.round(next * 100) / 100;
}

function FileReadProgressModal({ fileName, progress, isComplete = false, extractionStage = 0, onCancel }) {
  const fillWidth = Math.min(100, Math.max(0, progress));
  const stageLabel = isComplete
    ? 'Scope extraction complete'
    : UPLOAD_STAGE_LABELS[Math.min(extractionStage, UPLOAD_STAGE_LABELS.length - 1)];

  return (
    <div className="file-read-overlay" role="dialog" aria-modal="true" aria-labelledby="file-read-title">
      <div className={`file-read-card${isComplete ? ' file-read-card--complete' : ''}`}>
        <div className="file-read-loader-head">
          <div className={`file-read-spinner${isComplete ? ' file-read-spinner--done' : ''}`} aria-hidden="true" />
          <span className="file-read-status-badge">
            {isComplete ? 'Upload complete' : 'Processing document'}
          </span>
        </div>
        <h2 id="file-read-title" className="file-read-title">
          {isComplete ? 'Your scope is ready' : 'Extracting scope from document…'}
        </h2>
        <p className="file-read-subtitle">
          {isComplete
            ? 'Opening the extracted roles, modules, and features.'
            : 'Building roles, modules, and features. This may take a minute.'}
        </p>
        {fileName ? (
          <p className="file-read-filename" title={fileName}>
            {fileName}
          </p>
        ) : null}
        <div className="file-read-progress-block">
          <div className="file-read-bar-bg" aria-hidden="true">
            <div
              className={`file-read-bar-fill${isComplete ? ' file-read-bar-fill--complete' : ''}`}
              style={{ width: `${fillWidth}%` }}
            />
            {!isComplete ? <div className="file-read-bar-shimmer" style={{ width: `${fillWidth}%` }} /> : null}
          </div>
          <div className="file-read-progress-caption">
            <span className="file-read-caption-left">{stageLabel}</span>
            {onCancel && !isComplete ? (
              <button type="button" className="file-read-cancel" onClick={onCancel}>
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function MyDashboardModal({
  open,
  onClose,
  historyItems,
  onOpenChat,
  onDeleteItem,
  loading = false,
  error = '',
  openingId = null,
  deletingHistory = false,
}) {
  if (!open || typeof document === 'undefined') return null;

  const items = historyItems || [];

  return createPortal(
    <div className="dashboard-overlay" onClick={onClose} role="presentation">
      <div
        className="dashboard-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dashboard-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="dashboard-close" onClick={onClose} aria-label="Close dashboard">
          ×
        </button>
        <div className="dashboard-header">
          <div className="dashboard-header-copy">
            <span className="dashboard-kicker">History</span>
            <h2 id="dashboard-title" className="dashboard-title">Project workspace overview</h2>
            <p className="dashboard-subtitle">
              Review your full chat history and jump back into any past estimation.
            </p>
          </div>
        </div>
        <div className="dashboard-grid">
          <section className="dashboard-card">
            <div className="dashboard-card-header">
              <h3>History</h3>
              <span>{loading ? '…' : items.length}</span>
            </div>
            {loading ? (
              <div className="dashboard-empty">Loading history…</div>
            ) : error ? (
              <div className="dashboard-empty">{error}</div>
            ) : items.length > 0 ? (
              <div className="dashboard-list">
                {items.map((item, index) => {
                  const canOpen = Boolean(item?.sessionId);
                  const isOpening = openingId != null && openingId === item.sessionId;

                  const itemInner = (
                    <>
                      <strong className="dashboard-history-title">{item.title}</strong>
                      {item.meta ? (
                        <span className="dashboard-history-tag">{item.meta}</span>
                      ) : null}
                      {isOpening ? <em>Opening…</em> : null}
                    </>
                  );

                  const deleteBtn = (
                    <button
                      type="button"
                      className="dashboard-list-item-delete"
                      aria-label="Remove this history item"
                      disabled={openingId != null || deletingHistory}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteItem?.(item);
                      }}
                    >
                      ×
                    </button>
                  );

                  if (canOpen) {
                    return (
                      <div key={`history-${item.sessionId}`} className="dashboard-list-item-wrap">
                        <div className="dashboard-list-item dashboard-list-item-with-delete">
                          {deleteBtn}
                          <button
                            type="button"
                            className="dashboard-list-item-hit dashboard-list-item-button"
                            disabled={openingId != null}
                            onClick={() => onOpenChat?.(item)}
                          >
                            {itemInner}
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={`history-${index}`} className="dashboard-list-item-wrap">
                      <div className="dashboard-list-item dashboard-list-item-with-delete">
                        {deleteBtn}
                        <div className="dashboard-list-item-hit dashboard-list-item-hit--static">{itemInner}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="dashboard-empty">
                No chat history yet. Start a conversation — it will show here with a project tag and platform.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Chatbot() {
  const { user, logout } = useAuth();
  const { sessionId, messages, isLoading, slot, sendMessage, clearChat, restoreChat, injectSystemMessage } = useChat();
  const { theme } = useTheme();
  const { toastMessage, showToast } = useToast();
  const [inputValue, setInputValue] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const messagesSectionRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const fileReaderRef = useRef(null);
  const fileReadIntervalRef = useRef(null);
  const extractionIntervalRef = useRef(null);
  const uploadAbortControllerRef = useRef(null);
  const uploadCancelStateRef = useRef({ cancelled: false });
  const dragCounterRef = useRef(0);

  const [fileReadOverlay, setFileReadOverlay] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileErrorMsg, setFileErrorMsg] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const fileErrorTimerRef = useRef(null);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [allUsersOpen, setAllUsersOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [allUsersRefreshKey, setAllUsersRefreshKey] = useState(0);
  const [chatHistories, setChatHistories] = useState([]);
  const [dashboardSavedLoading, setDashboardSavedLoading] = useState(false);
  const [dashboardSavedError, setDashboardSavedError] = useState('');
  const [openingHistoryId, setOpeningHistoryId] = useState(null);
  const [historyDeleteTarget, setHistoryDeleteTarget] = useState(null);
  const [historyDeleting, setHistoryDeleting] = useState(false);
  const [dashboardOwnerId, setDashboardOwnerId] = useState(() => {
    if (user?.id != null) return String(user.id);
    if (typeof window === 'undefined') return '';
    try {
      const id = window.localStorage.getItem(STORAGE_KEYS.DASHBOARD_OWNER_ID);
      return id?.trim() || '';
    } catch {
      return '';
    }
  });

  useEffect(() => {
    if (user?.id == null) return;
    const ownerId = String(user.id);
    setDashboardOwnerId(ownerId);
    try {
      window.localStorage.setItem(STORAGE_KEYS.DASHBOARD_OWNER_ID, ownerId);
    } catch {
      /* ignore storage errors */
    }
  }, [user]);
  const persistedEstimationIdRef = useRef(null);
  const [dashboardExports, setDashboardExports] = useState([]);
  const [dashboardRatings, setDashboardRatings] = useState([]);
  const inputValueRef = useRef('');
  inputValueRef.current = inputValue;

  const showFileError = (msg) => {
    if (fileErrorTimerRef.current) clearTimeout(fileErrorTimerRef.current);
    setFileErrorMsg(msg);
    fileErrorTimerRef.current = setTimeout(() => setFileErrorMsg(null), 3500);
  };

  const clearFileReadTimer = () => {
    if (fileReadIntervalRef.current != null) {
      clearInterval(fileReadIntervalRef.current);
      fileReadIntervalRef.current = null;
    }
  };

  const clearExtractionInterval = () => {
    if (extractionIntervalRef.current != null) {
      clearInterval(extractionIntervalRef.current);
      extractionIntervalRef.current = null;
    }
  };

  const startUploadProgress = (name) => {
    clearExtractionInterval();
    const startedAt = Date.now();
    setFileReadOverlay({
      name,
      progress: 0,
      extractionStage: 0,
      isComplete: false,
    });
    extractionIntervalRef.current = window.setInterval(() => {
      setFileReadOverlay((prev) => {
        if (!prev || prev.isComplete) return prev;
        const elapsed = Date.now() - startedAt;
        const next = computeUploadProgressTick(prev.progress, elapsed);
        return {
          ...prev,
          progress: next,
          extractionStage: getUploadStageIndex(next),
        };
      });
    }, UPLOAD_PROGRESS_TICK_MS);
  };

  const finishUploadProgress = () =>
    new Promise((resolve) => {
      clearExtractionInterval();
      setFileReadOverlay((prev) =>
        prev
          ? {
            ...prev,
            progress: 100,
            isComplete: true,
            extractionStage: UPLOAD_STAGE_LABELS.length - 1,
          }
          : prev
      );
      window.setTimeout(resolve, UPLOAD_COMPLETE_PAUSE_MS);
    });

  const handleCancelFileRead = () => {
    uploadCancelStateRef.current.cancelled = true;
    try {
      uploadAbortControllerRef.current?.abort();
    } catch (_) {
      /* ignore */
    }
    uploadAbortControllerRef.current = null;
    clearFileReadTimer();
    clearExtractionInterval();
    const r = fileReaderRef.current;
    fileReaderRef.current = null;
    setFileReadOverlay(null);
    try {
      r?.abort();
    } catch (_) {
      /* ignore */
    }
  };

  useEffect(() => {
    return () => {
      clearFileReadTimer();
      clearExtractionInterval();
      try {
        fileReaderRef.current?.abort();
      } catch (_) {
        /* ignore */
      }
      fileReaderRef.current = null;
    };
  }, []);

  const handleAttachFileClick = () => {
    fileInputRef.current?.click();
  };

  const validateSelectedDocument = (file) => {
    if (!file) return false;
    if (file.size > MAX_DOCUMENT_UPLOAD_BYTES) {
      window.alert(`File is too large. Please choose a file under ${MAX_DOCUMENT_UPLOAD_MB} MB.`);
      return false;
    }
    if (isLegacyDocFile(file.name)) {
      window.alert('Legacy Word .doc is not supported. Save as .docx or export to plain text (.txt).');
      return false;
    }
    if (!isSupportedDocumentUpload(file)) {
      showFileError(DOCUMENT_UPLOAD_ERROR_MESSAGE);
      return false;
    }
    return true;
  };

  const processFile = async (file) => {
    if (!file || !validateSelectedDocument(file)) return;

    const fileName = file.name;

    uploadCancelStateRef.current.cancelled = false;
    const ac = new AbortController();
    uploadAbortControllerRef.current = ac;
    startUploadProgress(fileName);

    try {
      const preamble = inputValueRef.current.trim();
      const uploadLabel = `📎 ${fileName}`;

      setInputValue('');
      await sendMessage('(document upload)', {
        documentFile: file,
        documentUserInput: preamble,
        documentUploadLabel: uploadLabel,
        abortSignal: ac.signal,
        uploadCancelRef: uploadCancelStateRef.current,
      });
      await finishUploadProgress();
      uploadAbortControllerRef.current = null;
    } catch (err) {
      console.error(err);
      if (!String(err?.message || '').toLowerCase().includes('aborted')) {
        window.alert(
          err?.message ||
          'Could not process the document. Check your connection and file format, or paste the text into the chat.'
        );
      }
    } finally {
      clearFileReadTimer();
      clearExtractionInterval();
      try {
        fileReaderRef.current?.abort();
      } catch (_) {
        /* ignore */
      }
      fileReaderRef.current = null;
      uploadAbortControllerRef.current = null;
      setFileReadOverlay(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);

    if (isLoading) return;

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (!validateSelectedDocument(file)) return;
    setPendingFile(file);
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !validateSelectedDocument(file)) return;
    setPendingFile(file);
  };

  const isDark = theme === 'dark';
  const stepProgress = useMemo(() => getStepInitializerState(messages), [messages]);
  const stepAnchors = useMemo(() => getStepMessageAnchors(messages), [messages]);
  const messageIndexToStepStages = useMemo(
    () => messageIndicesToStepStages(stepAnchors),
    [stepAnchors]
  );
  const userMessages = useMemo(
    () => messages.filter((message) => message.role === 'user'),
    [messages]
  );
  const hasUserMessages = userMessages.length > 0;
  const showMissionMap = useMemo(() => hasSubmittedProjectIdea(messages), [messages]);
  const lastAssistantMessage = useMemo(
    () => [...messages].reverse().find((message) => message.role === 'assistant'),
    [messages]
  );
  const composerPlaceholder = hasUserMessages
    ? 'Continue refining the project, ask a follow-up, or upload a brief...'
    : 'Describe the product you want to scope...';

  const historyItems = useMemo(
    () => normalizeChatHistoryList(chatHistories),
    [chatHistories]
  );

  useEffect(() => {
    if (!dashboardOpen) return undefined;
    let cancelled = false;
    setDashboardSavedLoading(true);
    setDashboardSavedError('');
    listChatHistories()
      .then((res) => {
        if (cancelled) return;
        const historyRows = Array.isArray(res?.data) ? res.data : [];
        setChatHistories(historyRows);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setChatHistories([]);
          setDashboardSavedError(err?.message || 'Could not load history.');
        }
      })
      .finally(() => {
        if (!cancelled) setDashboardSavedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dashboardOpen]);

  useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_DASHBOARD_CHAT_ARCHIVES_KEY);
    } catch {
      /* ignore */
    }
    try {
      setDashboardExports(readStoredItems(DASHBOARD_EXPORTS_KEY));
      setDashboardRatings(readStoredItems(DASHBOARD_RATINGS_KEY));
    } catch {
      setDashboardExports([]);
      setDashboardRatings([]);
    }
  }, []);

  const handleDashboardSave = async () => {
    try {
      const res = await listChatHistories();
      setChatHistories(Array.isArray(res?.data) ? res.data : []);
      setDashboardSavedError('');
    } catch {
      /* ignore background refresh */
    }
  };

  const handleDashboardExport = (item) => {
    setDashboardExports(prependStoredItem(DASHBOARD_EXPORTS_KEY, item));
  };

  const handleDashboardRating = (item) => {
    setDashboardRatings(prependStoredItem(DASHBOARD_RATINGS_KEY, item));
  };

  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    node.style.height = '0px';
    const nextHeight = Math.min(node.scrollHeight, 180);
    node.style.height = `${Math.max(nextHeight, 32)}px`;
  }, [inputValue]);

  const handleStepClick = (stageId) => {
    const root = messagesSectionRef.current;
    if (!root || !stageId) return;
    const safe = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(stageId) : stageId;
    const sub = root.querySelector(`[data-chat-subanchor="${safe}"]`);
    const el = sub || root.querySelector(`[data-stages~="${safe}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Inject system message when page opens, reloads, or starts fresh (when there are no messages)
  useEffect(() => {
    if (messages.length === 0) {
      injectSystemMessage(SYSTEM_START_MESSAGE);
    }
  }, [messages.length, injectSystemMessage]);

  // Global scroll: all chat flows update `messages` / `isLoading` via useChat.sendMessage.
  // While waiting: top of "Thinking…". When a reply lands: top of the latest bubble (any flow).
  useEffect(() => {
    const root = messagesSectionRef.current;
    if (!root) return;

    const frame = requestAnimationFrame(() => {
      if (isLoading) {
        const typing = root.querySelector('[data-chat-typing="true"]');
        if (typing) {
          typing.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }

      const last = messages[messages.length - 1];
      if (last?.id) {
        const safeId =
          typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(last.id) : last.id;
        const el = root.querySelector(`[data-message-id="${safeId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [messages, isLoading]);

  const handleSend = () => {
    if (isLoading) return;
    if (pendingFile) {
      const file = pendingFile;
      setPendingFile(null);
      processFile(file);
    } else if (inputValue.trim()) {
      sendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };


  const handleConfirmScope = (updatedProjectScope, actionLabel = 'Confirm') => {
    sendMessage(actionLabel || 'Confirm', { updatedProjectScope });
  };

  const handleOptionClick = (option, optionsOrSlot = null) => {
    // Support both old format (explicitSlot) and new format (options object)
    const options = typeof optionsOrSlot === 'object' && optionsOrSlot !== null
      ? optionsOrSlot
      : { explicitSlot: optionsOrSlot };
    return sendMessage(option, options);
  };

  const handleOpenArchivedChat = async (item) => {
    if (!item?.sessionId) return;
    if (isLoading || openingHistoryId != null) {
      showToast('Please wait for the current response to finish.');
      return;
    }

    setOpeningHistoryId(item.sessionId);
    try {
      const res = await getChatHistory(item.sessionId);
      const data = res?.data ?? res;
      const historyMessages = extractHistoryMessages(data);
      if (historyMessages.length === 0) {
        showToast('This chat has no saved messages yet.');
        return;
      }

      restoreChat({
        sessionId: extractHistorySessionId(data, item.sessionId),
        slot: extractHistorySlot(data, historyMessages),
        messages: sanitizeArchiveMessages(historyMessages),
      });
      persistedEstimationIdRef.current = null;
      setInputValue('');
      setDashboardOpen(false);
      setIsSidebarOpen(false);
    } catch (err) {
      console.error(err);
      showToast(err?.message || 'Could not open this chat.');
    } finally {
      setOpeningHistoryId(null);
    }
  };

  const handleDeleteHistoryItem = (item) => {
    if (!item?.sessionId) return;
    setHistoryDeleteTarget(item);
  };

  const handleConfirmDeleteHistoryItem = async () => {
    if (historyDeleting) return;
    const item = historyDeleteTarget;
    if (!item?.sessionId) {
      setHistoryDeleteTarget(null);
      return;
    }
    setHistoryDeleting(true);
    try {
      await deleteChatHistory(item.sessionId);
      setChatHistories((prev) =>
        prev.filter((row) => (row.session_id ?? row.sessionId) !== item.sessionId)
      );
      setHistoryDeleteTarget(null);
    } catch (err) {
      console.error(err);
      showToast(err?.message || 'Could not delete this history item.');
    } finally {
      setHistoryDeleting(false);
    }
  };

  const handleNewChat = async () => {
    handleCancelFileRead();
    try {
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      /* ignore */
    }
    persistedEstimationIdRef.current = null;
    setInputValue('');
    setDashboardOpen(false);
    try {
      window.sessionStorage.removeItem(ESTIMATION_REVIEW_SEEN_KEY);
    } catch {
      /* ignore */
    }
    await clearChat();
    setIsSidebarOpen(false);
  };

  return (
    <div className={`chatbot-container ${isDark ? 'dark-theme' : 'light-theme'}`}>
      {fileReadOverlay &&
        typeof document !== 'undefined' &&
        createPortal(
          <FileReadProgressModal
            fileName={fileReadOverlay.name}
            progress={fileReadOverlay.progress}
            isComplete={fileReadOverlay.isComplete}
            extractionStage={fileReadOverlay.extractionStage ?? 0}
            onCancel={handleCancelFileRead}
          />,
          document.body
        )}
      <MyDashboardModal
        open={dashboardOpen}
        onClose={() => setDashboardOpen(false)}
        historyItems={historyItems}
        onOpenChat={handleOpenArchivedChat}
        onDeleteItem={handleDeleteHistoryItem}
        loading={dashboardSavedLoading}
        error={dashboardSavedError}
        openingId={openingHistoryId}
        deletingHistory={historyDeleting}
      />
      <ConfirmDialog
        open={Boolean(historyDeleteTarget)}
        title="Delete chat?"
        message="Are you sure, you want to delete this chat??"
        confirmLabel={historyDeleting ? 'Deleting…' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeleteHistoryItem}
        onCancel={() => {
          if (historyDeleting) return;
          setHistoryDeleteTarget(null);
        }}
      />
      <AllUsersModal
        open={allUsersOpen}
        onClose={() => setAllUsersOpen(false)}
        refreshKey={allUsersRefreshKey}
      />
      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onUpdated={(message) => showToast(message)}
      />
      {toastMessage &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="module-toast">
            <div className="module-toast-content">
              <span className="module-toast-icon">✓</span>
              <span className="module-toast-message">{toastMessage}</span>
            </div>
          </div>,
          document.body
        )}
      {fileErrorMsg &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="module-toast file-error-toast">
            <div className="module-toast-content">
              <span className="module-toast-icon file-error-icon">✕</span>
              <span className="module-toast-message">{fileErrorMsg}</span>
            </div>
          </div>,
          document.body
        )}
      {/* Animated mesh gradient backgrounds */}
      <div className="mesh-gradient-bg">
        <div className="mesh-gradient mesh-gradient-1"></div>
        <div className="mesh-gradient mesh-gradient-2"></div>
        <div className="mesh-gradient mesh-gradient-3"></div>
      </div>

      {/* Sidebar */}
      <aside className={`sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Logo className="logo-img" />
          <div className="brand-info">
            <span className="brand-name">Concetto Estimator</span>
            <span className="brand-tagline">{user?.full_name || user?.email || 'Your AI Teammate'}</span>
          </div>
        </div>

        <div className="sidebar-actions">
          <button
            type="button"
            className="sidebar-action-btn glass-button sidebar-dashboard-btn"
            onClick={() => setDashboardOpen(true)}
            aria-label="Open my dashboard"
            title="Open my dashboard"
          >
            <span className="sidebar-dashboard-icon" aria-hidden="true">◫</span>
            <span className="sidebar-action-label">History</span>
          </button>
        </div>

        {showMissionMap && (
          <div className="sidebar-step-initializer">
            <StepInitializer
              progress={stepProgress}
              compact
              variant="sidebar"
              onStepClick={handleStepClick}
              stepAnchors={stepAnchors}
            />
          </div>
        )}

        <SidebarUserFooter
          onOpenAllUsers={() => setAllUsersOpen(true)}
          onOpenProfile={() => setProfileOpen(true)}
          onLogout={logout}
        />
      </aside>

      {/* Mobile sidebar overlay */}
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>
      )}

      {/* Main chat area */}
      <main
        className={`chat-main${isDragOver ? ' drag-over' : ''}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <div className="drag-drop-overlay" aria-hidden="true">
            <div className="drag-drop-overlay-inner">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <p>Drop your .docx, .txt, or .pdf file here</p>
            </div>
          </div>
        )}
        {/* Header */}
        <header className="chat-header glass-header">
          <button
            className="mobile-menu-btn glass-button"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <div className="chat-header-info">
            <div className="chat-header-title-wrap">
              <h1 className="chat-header-title">Concetto Estimator</h1>
              <span className="chat-header-badge">Scope Studio</span>
            </div>
            <p
              className="chat-header-subtitle"
              title="Professional scoping assistant for turning rough ideas into delivery-ready structure."
            >
              Professional scoping assistant for turning rough ideas into delivery-ready structure.
            </p>
          </div>
          <div className="chat-header-status" aria-label="Session actions">
            <button
              type="button"
              className="chat-header-new-chat-btn"
              onClick={handleNewChat}
              aria-label="New Estimation"
              title="New estimation"
            >
              <span className="chat-header-new-chat-icon" aria-hidden="true">+</span>
              <span>New Estimation</span>
            </button>
          </div>
        </header>

        {/* Messages area */}
        <div className="messages-section" ref={messagesSectionRef}>
          <div className="messages-inner">
            {!hasUserMessages && (
              <div className="guided-prompts">
                {[
                  'Build a food delivery app',
                  'Create internal admin dashboard',
                  'Design booking platform',
                ].map((prompt) => (
                  <button
                    key={prompt}
                    className="intro-prompt-chip"
                    onClick={() => setInputValue(prompt)}
                    type="button"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}
            {messages.map((message, index) => (
              <Message
                key={message.id || index}
                message={message}
                messageIndex={index}
                onOptionClick={handleOptionClick}
                onConfirmScope={handleConfirmScope}
                messages={messages}
                stepAnchorStages={messageIndexToStepStages[index]}
                sessionId={sessionId}
                chatSlot={slot}
                onDashboardSave={handleDashboardSave}
                onDashboardExport={handleDashboardExport}
                onDashboardRating={handleDashboardRating}
                dashboardOwnerId={dashboardOwnerId}
                persistedEstimationIdRef={persistedEstimationIdRef}
              />
            ))}
            {isLoading && <TypingIndicator />}
          </div>
        </div>

        {/* Input area */}
        <div className="input-area glass-input-area">
          {pendingFile && (
            <div className="pending-file-chip">
              <div className="pending-file-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div className="pending-file-info">
                <span className="pending-file-name">{pendingFile.name}</span>
                <span className="pending-file-type">{getDocumentTypeLabel(pendingFile.name)}</span>
              </div>
              <button
                className="pending-file-remove"
                onClick={() => setPendingFile(null)}
                type="button"
                aria-label="Remove file"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}
          <div className="input-wrapper">
            <input
              ref={fileInputRef}
              type="file"
              accept={DOCUMENT_UPLOAD_ACCEPT}
              className="visually-hidden-file-input"
              aria-hidden="true"
              tabIndex={-1}
              onChange={handleFileSelected}
            />
            <button
              type="button"
              className="upload-btn"
              onClick={handleAttachFileClick}
              disabled={isLoading}
              aria-label="Attach .docx, .txt, or .pdf file"
              title="Attach .docx, .txt, or .pdf"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="8" y1="2" x2="8" y2="14" />
                <line x1="2" y1="8" x2="14" y2="8" />
              </svg>
            </button>
            <textarea
              ref={inputRef}
              className="message-input glass-input"
              placeholder={composerPlaceholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              rows="1"
              disabled={isLoading}
            />
            <button
              type="button"
              className="send-btn"
              onClick={handleSend}
              disabled={(!inputValue.trim() && !pendingFile) || isLoading}
            >
              <SendIcon />
            </button>
          </div>
          <p className="input-hint">Review the generated scope and estimation before using it for delivery commitments.</p>
        </div>
      </main>
    </div>
  );
}
